import React, { useState, useEffect } from 'react';
import {
  X, Mail, ChevronDown, Phone, MapPin, Briefcase, FileText, Info, Globe, UserCheck,
  Paperclip, UploadCloud, Trash2, Calendar, User, Clock, Activity, File, ExternalLink, Plus, Copy, Check, Palette, Settings,
  ShieldCheck, ShieldAlert, DollarSign, Calculator, AlertTriangle, CheckSquare, Layers, Send, Loader2, Archive,
  FileSignature, FileCheck2, CreditCard, Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Lead, PipelineType, PIPELINE_BOARD_CATEGORIES, STATUS_DISPLAY_LABELS, DEFAULT_STATUS_COLORS,
  AUTHORIZED_SALESPERSONS, SERVICE_TYPES, INDUSTRIES, OUTBOUND_STATES, OUTBOUND_WAREHOUSES, DAYS_OF_SERVICE, UNITS_OF_MEASURE, QUOTE_FREQUENCIES,
  ServiceType, IndustryType, OutboundState, OutboundWarehouse, DayOfService, UnitOfMeasure, QuoteFrequency,
  Shipper, Consignee, LeadType, LEAD_TYPES, LeadDocument, LeadLane, PAYMENT_TERMS_OPTIONS
} from '../types';
import { formatFullDeliveryView, formatFullQuoteView, calculateEstimatedDealValue } from '../lib/quotingEngine';
import { calculatePipelineState } from '../lib/pipelineEngine';
import { auth } from '../lib/firebase';
import { CRMUser, UserRole, fetchShippers, fetchConsignees, authedFetch, downloadLeadDocument } from '../lib/db';
import { sendGmailEmail, isGoogleTokenExpired, notifyGoogleTokenExpired } from '../lib/googleApi';
import { createFollowUpEvent } from '../lib/googleWorkspace';
import Tooltip from './Tooltip';
import LeadFormsTab from './LeadFormsTab';

interface SlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (lead: Omit<Lead, 'id'> & { id?: string }) => void;
  editingLead?: Lead | null;
  defaultPipeline?: PipelineType | null;
  statusColors: Record<string, string>;
  onSaveStatusColors: (newColors: Record<string, string>) => void;
  users: CRMUser[];
  showToast?: (msg: string, leadId?: string) => void;
  accessToken?: string | null;
  userRole?: UserRole;
}

const DEFAULT_SHIPPERS: Shipper[] = [
  { shipperName: 'ALACHUA FERNERY LLC', shipperAccount: 'SH1025', shipperEmail: 'shipping@alachuafern.com', shipperAddress: '102 Greenery Way', shipperCity: 'Alachua', shipperState: 'FL', shipperPhone: '386-555-0199' },
  { shipperName: 'MIAMI FOLIAGE GROWERS', shipperAccount: 'SH2044', shipperEmail: 'orders@miamifoliage.com', shipperAddress: '5500 SW 87th Ave', shipperCity: 'Miami', shipperState: 'FL', shipperPhone: '305-555-0231' },
  { shipperName: 'SOUTHERN CALIFORNIA SOIL CO', shipperAccount: 'SH3002', shipperEmail: 'logistics@socalsoil.com', shipperAddress: '900 Desert Road', shipperCity: 'Oxnard', shipperState: 'CA', shipperPhone: '805-555-9011' },
  { shipperName: 'COSTA FARMS LLC', shipperAccount: 'SH4010', shipperEmail: 'info@costafarms.com', shipperAddress: '21800 SW 162nd Ave', shipperCity: 'Miami', shipperState: 'FL', shipperPhone: '305-247-3232' },
  { shipperName: 'SUNSHINE STATE GROWERS', shipperAccount: 'SH5022', shipperEmail: 'sales@sunshinegrowers.com', shipperAddress: '1400 Nursery Rd', shipperCity: 'Apopka', shipperState: 'FL', shipperPhone: '407-555-8810' }
];

const DEFAULT_CONSIGNEES: Consignee[] = [
  { consigneeAccount: 'B010081', consigneeName: 'BOSTON WHOLESALE FLORAL', consigneeAddress: '400 Market Street', receivingWindow: '06:00 - 12:00', consigneeCity: 'Boston', consigneeState: 'MA', consigneeZip: '02108', consigneeEmail: 'bostonfloral@outlook.com', consigneePhone: '617-555-8812' },
  { consigneeAccount: 'B010155', consigneeName: 'MID-ATLANTIC GREENS', consigneeAddress: '88 Logistics Blvd', receivingWindow: '08:00 - 16:00', consigneeCity: 'Norfolk', consigneeState: 'VA', consigneeZip: '23501', consigneeEmail: 'receiving@midatlanticgreens.com', consigneePhone: '757-555-4421' },
  { consigneeAccount: 'B012000', consigneeName: 'SEWELL GARDEN CENTER', consigneeAddress: '12 Village Green', receivingWindow: '07:30 - 13:30', consigneeCity: 'Sewell', consigneeState: 'NJ', consigneeZip: '08080', consigneeEmail: 'contact@sewellgardens.com', consigneePhone: '856-555-0122' },
  { consigneeAccount: 'C015099', consigneeName: 'METRO FLORAL IMPORTS', consigneeAddress: '500 Industrial Pkwy', receivingWindow: '08:00 - 17:00', consigneeCity: 'Newark', consigneeState: 'NJ', consigneeZip: '07102', consigneeEmail: 'orders@metrofloral.com', consigneePhone: '973-555-9080' },
  { consigneeAccount: 'C022011', consigneeName: 'PACIFIC NORTHWEST FLORAL', consigneeAddress: '1200 Harbor Ave', receivingWindow: '07:00 - 15:00', consigneeCity: 'Seattle', consigneeState: 'WA', consigneeZip: '98134', consigneeEmail: 'receiving@pnwfloral.com', consigneePhone: '206-555-4490' }
];

export default function SlideOver({
  isOpen,
  onClose,
  onSave,
  editingLead,
  defaultPipeline,
  statusColors,
  onSaveStatusColors,
  users,
  showToast,
  accessToken,
  userRole = 'admin',
}: SlideOverProps) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [dealValue, setDealValue] = useState(0);
  const [pipeline, setPipeline] = useState<PipelineType>('New Leads');
  const [status, setStatus] = useState<string>('New');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpCompletedAt, setFollowUpCompletedAt] = useState('');
  const [followUpCompleted, setFollowUpCompleted] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [syncToCalendar, setSyncToCalendar] = useState(true);

  // Rich CRM fields
  const [owner, setOwner] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [careOf, setCareOf] = useState('');
  const [billToAccountNumber, setBillToAccountNumber] = useState('');
  const [leadNotes, setLeadNotes] = useState('');
  const [location, setLocation] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [nextSteps, setNextSteps] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState('');
  const [industry, setIndustry] = useState('');
  const [appLink, setAppLink] = useState('');
  const [quoteDetails, setQuoteDetails] = useState('');
  const [quickNotes, setQuickNotes] = useState(''); // locked history of prior notes
  const [newNote, setNewNote] = useState(''); // note being written now

  // Operational & Address Fields
  const [billToType, setBillToType] = useState<'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To'>('Bill To/Consignee');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [shipperName, setShipperName] = useState('');
  const [shipperAccount, setShipperAccount] = useState('');
  const [consigneeName, setConsigneeName] = useState('');
  const [consigneeAccount, setConsigneeAccount] = useState('');
  const [consigneeCity, setConsigneeCity] = useState('');
  const [consigneeState, setConsigneeState] = useState('');
  const [consigneeZip, setConsigneeZip] = useState('');
  const [useConsigneeAsBillTo, setUseConsigneeAsBillTo] = useState(false);
  const [useShipperAsBillTo, setUseShipperAsBillTo] = useState(false);
  const [sameAsConsigneeAddress, setSameAsConsigneeAddress] = useState(false);
  const [sameAsCompany, setSameAsCompany] = useState(false);
  const [routingOpsApproved, setRoutingOpsApproved] = useState(false);

  // Multi-select Selections
  const [leadType, setLeadType] = useState<LeadType[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [industries, setIndustries] = useState<IndustryType[]>([]);
  const [outboundStates, setOutboundStates] = useState<OutboundState[]>([]);
  const [outboundWarehouses, setOutboundWarehouses] = useState<OutboundWarehouse[]>([]);
  const [daysOfService, setDaysOfService] = useState<DayOfService[]>([]);

  // Quoting Engine Fields
  const [quoteDollarAmount, setQuoteDollarAmount] = useState<number>(0);
  const [uom, setUom] = useState<UnitOfMeasure | ''>('Cube');
  const [handlingFeeAmount, setHandlingFeeAmount] = useState<number>(0);
  const [noHandlingFee, setNoHandlingFee] = useState(false);
  const [fuelSurcharge, setFuelSurcharge] = useState(false);
  const [fuelSurchargePercent, setFuelSurchargePercent] = useState<number>(0);
  const [noFuelSurcharge, setNoFuelSurcharge] = useState(false);
  const [forecastVolume, setForecastVolume] = useState<number>(1);
  const [quoteFrequency, setQuoteFrequency] = useState<QuoteFrequency | ''>('Weekly');
  const [minimumMonthlyAmount, setMinimumMonthlyAmount] = useState<number>(0);
  const [minimumShipmentAmount, setMinimumShipmentAmount] = useState<number>(0);
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [depositAmount, setDepositAmount] = useState<number | null>(null);

  // Multi-Lane State (Change 6)
  const createBlankLane = (laneNumber: number, leadId?: string): LeadLane => ({
    id: `lane-${Date.now()}-${laneNumber}-${Math.random().toString(36).slice(2, 6)}`,
    leadId: leadId || '',
    laneNumber,
    approvalStatus: 'pending',
    sentToOpsAt: '',
    sentToOpsBy: '',
    approvedAt: '',
    approvedBy: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    sameAsConsignee: false,
    shipperName: '',
    consigneeName: '',
    rateAgreementRole: 'Bill To/Consignee',
    consigneeCity: '',
    consigneeState: '',
    consigneeZip: '',
    serviceTypes: [],
    industries: [],
    outboundStates: [],
    outboundWarehouses: [],
    daysOfService: [],
    forecastVolume: 1,
    dollarAmount: 0,
    uom: 'Cube',
    serviceFrequency: 'Weekly',
    handlingFee: 0,
    noHandlingFee: false,
    fuelSurchargePercent: 0,
    noFuelSurcharge: false,
    minimumShipmentAmount: 0,
    paymentTerms: '',
    depositAmount: null,
    estimatedDealValue: 0,
    fullDeliveryView: '',
    fullQuoteView: '',
  });
  const [lanes, setLanes] = useState<LeadLane[]>(() => [createBlankLane(1)]);

  const updateLane = (idx: number, patch: Partial<LeadLane>) => {
    setLanes((prev) => {
      const next = prev.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return next;
    });
  };

  const handleAddLane = () => {
    const nextNumber = lanes.length + 1;
    const newLane = createBlankLane(nextNumber, editingLead?.id);
    setLanes((prev) => [...prev, newLane]);
    setTimeout(() => {
      const el = document.getElementById(`lane-card-${nextNumber}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  const handleRemoveLane = (idx: number) => {
    if (lanes.length <= 1) return;
    const laneNumber = idx + 1;
    if (!window.confirm(`Remove Lane ${laneNumber}? This cannot be undone.`)) return;
    setLanes((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, laneNumber: i + 1 })));
  };

  // Quote Request & Operations Approval Audit Trail
  const [quoteRequestedBy, setQuoteRequestedBy] = useState('');
  const [quoteRequestedAt, setQuoteRequestedAt] = useState('');
  const [routingApprovedBy, setRoutingApprovedBy] = useState('');
  const [routingApprovedAt, setRoutingApprovedAt] = useState('');
  const [sendingOpsEmail, setSendingOpsEmail] = useState(false);

  // Onboarding Milestone Checkboxes (Change 3)
  const [creditAppSentAt, setCreditAppSentAt] = useState<string | null>(null);
  const [creditAppSentBy, setCreditAppSentBy] = useState<string | null>(null);
  const [rateAgreementSentAt, setRateAgreementSentAt] = useState<string | null>(null);
  const [rateAgreementSentBy, setRateAgreementSentBy] = useState<string | null>(null);
  const [internalEmailsSentAt, setInternalEmailsSentAt] = useState<string | null>(null);
  const [internalEmailsSentBy, setInternalEmailsSentBy] = useState<string | null>(null);
  const [firstDeliveryFollowUpAt, setFirstDeliveryFollowUpAt] = useState<string | null>(null);
  const [firstDeliveryFollowUpBy, setFirstDeliveryFollowUpBy] = useState<string | null>(null);

  // Shipper / Consignee Search & Autocomplete states
  const [shippersList, setShippersList] = useState<Shipper[]>([]);
  const [consigneesList, setConsigneesList] = useState<Consignee[]>([]);
  const [consigneeSuggestions, setConsigneeSuggestions] = useState<Consignee[]>([]);
  const [showConsigneeDropdown, setShowConsigneeDropdown] = useState(false);
  const [shipperSuggestions, setShipperSuggestions] = useState<Shipper[]>([]);
  const [showShipperDropdown, setShowShipperDropdown] = useState(false);

  // Load Shippers & Consignees on component mount
  useEffect(() => {
    let active = true;
    Promise.all([fetchShippers(), fetchConsignees()])
      .then(([shippers, consignees]) => {
        if (!active) return;
        setShippersList(shippers || []);
        setConsigneesList(consignees || []);
      })
      .catch((err) => console.warn("Failed to fetch shippers/consignees:", err));

    return () => { active = false; };
  }, []);

  useEffect(() => {
    const handleGlobalClick = () => {
      setShowConsigneeDropdown(false);
      setShowShipperDropdown(false);
    };
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  // Direct Bill-To / Company Name Input Handler
  const handleCompanyInputChange = (val: string) => {
    const capsVal = val.toUpperCase();
    setCompany(capsVal);

    if (useConsigneeAsBillTo && capsVal !== (consigneeName || '').toUpperCase()) {
      setUseConsigneeAsBillTo(false);
    }
    if (useShipperAsBillTo && capsVal !== (shipperName || '').toUpperCase()) {
      setUseShipperAsBillTo(false);
    }
  };

  // Consignee Checkbox Toggle Handler
  const handleToggleUseConsigneeAsBillTo = (checked: boolean) => {
    setUseConsigneeAsBillTo(checked);
    if (checked) {
      setUseShipperAsBillTo(false);
      setBillToType('Bill To/Consignee');
      if (consigneeName && consigneeName.trim() !== '') {
        setCompany(consigneeName.toUpperCase());
      }
      if (consigneeAccount && consigneeAccount.trim() !== '') {
        setBillToAccountNumber(consigneeAccount.toUpperCase());
      }
    }
  };

  // Shipper Checkbox Toggle Handler
  const handleToggleUseShipperAsBillTo = (checked: boolean) => {
    setUseShipperAsBillTo(checked);
    if (checked) {
      setUseConsigneeAsBillTo(false);
      setBillToType('Bill To/Shipper');
      if (shipperName && shipperName.trim() !== '') {
        setCompany(shipperName.toUpperCase());
      }
      if (shipperAccount && shipperAccount.trim() !== '') {
        setBillToAccountNumber(shipperAccount.toUpperCase());
      }
    }
  };

  // Consignee Input & Autocomplete Change Handler
  const handleConsigneeNameChange = (val: string) => {
    const capsVal = val.toUpperCase();
    setConsigneeName(capsVal);
    if (useConsigneeAsBillTo) {
      setCompany(capsVal);
    }

    if (!capsVal.trim()) {
      setConsigneeSuggestions([]);
      setShowConsigneeDropdown(false);
      return;
    }

    const query = capsVal.trim();
    const activeConsignees = consigneesList.length > 0 ? consigneesList : DEFAULT_CONSIGNEES;
    const seen = new Set<string>();
    const results: Consignee[] = [];

    activeConsignees.forEach((c) => {
      const cName = (c.consigneeName || '').toUpperCase();
      const cAcc = (c.consigneeAccount || '').toUpperCase();
      const key = `${cAcc}-${cName}`;
      if ((cName.includes(query) || cAcc.includes(query)) && !seen.has(key)) {
        seen.add(key);
        results.push(c);
      }
    });

    setConsigneeSuggestions(results.slice(0, 10));
    setShowConsigneeDropdown(results.length > 0);
  };

  const handleSelectConsignee = (item: Consignee) => {
    const cName = (item.consigneeName || '').toUpperCase();
    const cAcc = (item.consigneeAccount || '').toUpperCase();
    setConsigneeName(cName);
    if (useConsigneeAsBillTo) {
      setCompany(cName);
      if (cAcc) setBillToAccountNumber(cAcc);
    }
    if (cAcc) {
      setConsigneeAccount(cAcc);
    }
    if (item.consigneeAddress && !street) {
      setStreet(item.consigneeAddress);
    }
    if (item.consigneeCity) {
      setConsigneeCity(item.consigneeCity);
      if (!city) setCity(item.consigneeCity);
    }
    if (item.consigneeState) {
      setConsigneeState(item.consigneeState);
      if (!state) setState(item.consigneeState);
    }
    if (item.consigneeZip) {
      setConsigneeZip(item.consigneeZip);
      if (!zipCode) setZipCode(item.consigneeZip);
    }
    // Also populate Lane 1 if its delivery fields are empty
    setLanes((prev) => {
      if (prev.length === 0) return prev;
      const l0 = prev[0];
      const patch: Partial<LeadLane> = {};
      if (!l0.consigneeName && cName) patch.consigneeName = cName;
      if (!l0.street && item.consigneeAddress) patch.street = item.consigneeAddress;
      if (!l0.city && item.consigneeCity) patch.city = item.consigneeCity;
      if (!l0.state && item.consigneeState) patch.state = item.consigneeState;
      if (!l0.zipCode && item.consigneeZip) patch.zipCode = item.consigneeZip;
      if (item.consigneeCity) patch.consigneeCity = item.consigneeCity;
      if (item.consigneeState) patch.consigneeState = item.consigneeState;
      if (item.consigneeZip) patch.consigneeZip = item.consigneeZip;
      if (Object.keys(patch).length === 0) return prev;
      return [{ ...l0, ...patch }, ...prev.slice(1)];
    });
    setShowConsigneeDropdown(false);
  };

  // Shipper Input & Autocomplete Change Handler
  const handleShipperNameChange = (val: string) => {
    const capsVal = val.toUpperCase();
    setShipperName(capsVal);
    if (useShipperAsBillTo) {
      setCompany(capsVal);
    }

    if (!capsVal.trim()) {
      setShipperSuggestions([]);
      setShowShipperDropdown(false);
      return;
    }

    const query = capsVal.trim();
    const activeShippers = shippersList.length > 0 ? shippersList : DEFAULT_SHIPPERS;
    const seen = new Set<string>();
    const results: Shipper[] = [];

    activeShippers.forEach((s) => {
      const sName = (s.shipperName || s.shippername || '').toUpperCase();
      const sAcc = (s.shipperAccount || s.shipper_account || s.a4shipid || '').toUpperCase();
      const key = `${sAcc}-${sName}`;
      if ((sName.includes(query) || sAcc.includes(query)) && !seen.has(key)) {
        seen.add(key);
        results.push(s);
      }
    });

    setShipperSuggestions(results.slice(0, 10));
    setShowShipperDropdown(results.length > 0);
  };

  const handleSelectShipper = (item: Shipper) => {
    const sName = (item.shipperName || item.shippername || '').toUpperCase();
    const sAcc = (item.shipperAccount || item.shipper_account || item.a4shipid || '').toUpperCase();
    setShipperName(sName);
    if (useShipperAsBillTo) {
      setCompany(sName);
      if (sAcc) setBillToAccountNumber(sAcc);
    }
    if (sAcc) {
      setShipperAccount(sAcc);
    }
    setLanes((prev) => {
      if (prev.length === 0) return prev;
      const l0 = prev[0];
      if (!l0.shipperName && sName) {
        return [{ ...l0, shipperName: sName }, ...prev.slice(1)];
      }
      return prev;
    });
    setShowShipperDropdown(false);
  };

  const handleSendOpsEmail = async (laneIdx: number = 0) => {
    const requester = auth.currentUser?.displayName || auth.currentUser?.email || 'Sales Representative';
    const now = new Date().toISOString();
    const lane = lanes[laneIdx] || lanes[0] || createBlankLane(1);
    const laneNumber = laneIdx + 1;
    const laneLocLabel = [lane.city, lane.state].filter(Boolean).join(', ') || 'Delivery Location';
    const laneHeaderStr = `Lane ${laneNumber}: ${laneLocLabel}`;

    updateLane(laneIdx, {
      sentToOpsBy: requester,
      sentToOpsAt: now,
      approvalStatus: lane.approvalStatus === 'approved' ? 'approved' : 'sent',
    });
    if (laneIdx === 0) {
      setQuoteRequestedBy(requester);
      setQuoteRequestedAt(now);
    }

    const estVal = calculateEstimatedDealValue(
      lane.dollarAmount || 0,
      lane.uom || '',
      lane.handlingFee || 0,
      Boolean(lane.noHandlingFee),
      (lane.fuelSurchargePercent || 0) > 0 && !lane.noFuelSurcharge,
      lane.serviceFrequency || '',
      lane.minimumShipmentAmount || 0,
      lane.forecastVolume || 1,
      lane.fuelSurchargePercent || 0,
      Boolean(lane.noFuelSurcharge)
    );

    const recordLink = `${window.location.origin}/?leadId=${editingLead?.id || ''}`;
    const subject = `Quote Routing Approval Request (${laneHeaderStr}): ${company || name || 'Customer Lead'}`;
    const textBody = `Hello Operations / Routing Team,\n\nQuote routing approval is requested for lead "${company || name}" (${laneHeaderStr}).\n\nDelivery Specs & Quote Summary:\n- Lane: ${laneHeaderStr}\n- Contact: ${contactPerson || name || 'N/A'}\n- Street Address: ${lane.street || 'N/A'}\n- City/State/Zip: ${lane.city || ''}, ${lane.state || ''} ${lane.zipCode || ''}\n- Services: ${(lane.serviceTypes || []).join(', ') || 'N/A'}\n- Quote Amount: $${lane.dollarAmount || 0} / ${lane.uom || 'N/A'} (${lane.serviceFrequency || 'N/A'})\n- Est. Monthly Deal Value: $${estVal.toLocaleString()}/mo\n\nOpen Record in CRM:\n${recordLink}\n\nRequested By: ${requester}\nDate: ${new Date().toLocaleString()}\n`;

    const htmlBody = `
      <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background-color: #ffffff; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);">
        <div style="background-color: #1a2b4c; padding: 20px; text-align: center; color: #ffffff;">
          <h2 style="margin: 0; font-size: 18px; font-weight: 800; letter-spacing: 0.5px;">Quote Routing Approval Request (${laneHeaderStr})</h2>
          <p style="margin: 4px 0 0 0; font-size: 12px; opacity: 0.85;">Armellini Logistics Operations &amp; Routing Gatekeeper</p>
        </div>
        <div style="padding: 24px; color: #334155; font-size: 14px; line-height: 1.6;">
          <p style="margin-top: 0; font-weight: 700; font-size: 15px;">Hello Operations / Routing Team,</p>
          <p>Quote routing approval is requested for lead: <strong style="color: #0f172a;">${company || name || 'Customer Lead'}</strong> (${laneHeaderStr}).</p>
          
          <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; margin: 18px 0; font-size: 13px;">
            <tr>
              <td style="padding: 16px;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600; width: 140px;">Lane:</td>
                    <td style="padding-bottom: 8px; color: #0f172a; font-weight: 700;">${laneHeaderStr}</td>
                  </tr>
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600; width: 140px;">Customer / Lead:</td>
                    <td style="padding-bottom: 8px; color: #0f172a; font-weight: 700;">${company || name}</td>
                  </tr>
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600;">Contact Person:</td>
                    <td style="padding-bottom: 8px; color: #0f172a;">${contactPerson || name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600;">Physical Address:</td>
                    <td style="padding-bottom: 8px; color: #0f172a;">${lane.street || 'N/A'}, ${lane.city || ''}, ${lane.state || ''} ${lane.zipCode || ''}</td>
                  </tr>
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600;">Services Requested:</td>
                    <td style="padding-bottom: 8px; color: #0f172a; font-weight: 600;">${(lane.serviceTypes || []).join(', ') || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td style="padding-bottom: 8px; color: #64748b; font-weight: 600;">Quote Amount:</td>
                    <td style="padding-bottom: 8px; color: #1e3a8a; font-weight: 800;">$${lane.dollarAmount || 0} / ${lane.uom || 'N/A'} (${lane.serviceFrequency || 'N/A'})</td>
                  </tr>
                  <tr>
                    <td style="color: #64748b; font-weight: 600;">Est. Monthly Value:</td>
                    <td style="color: #166534; font-weight: 800; font-family: monospace;">$${estVal.toLocaleString()} / mo</td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>

          <div style="text-align: center; margin: 24px 0;">
            <a href="${recordLink}" style="display: inline-block; padding: 12px 28px; background-color: #1a2b4c; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 700; font-size: 14px; box-shadow: 0 4px 6px -1px rgba(26, 43, 76, 0.2);">
              &rarr; Open Record in Armellini CRM
            </a>
          </div>

          <div style="border-top: 1px solid #f1f5f9; pt-16px; font-size: 11px; color: #64748b;">
            Requested by <strong>${requester}</strong> on ${new Date().toLocaleString()}
          </div>
        </div>
      </div>
    `;

    // Post to Google Chat Webhook
    const googleChatWebhookUrl = 'https://chat.googleapis.com/v1/spaces/AAQAaPZLRJA/messages?key=AIzaSyDdI0hCZtE6vySjMm-WEfRq3CPzqKqqsHI&token=4H5a-4m2XjashpN7afsvi-u-fcwCAVeUH7iR18PNhEA';
    const estMonthlyVal = estVal.toLocaleString();

    const webhookPayload = {
      text: `*🚨 Quote Routing Approval Request (${laneHeaderStr})*\n\n` +
        `*Lane:* ${laneHeaderStr}\n` +
        `*Customer / Lead:* ${company || name || 'Customer Lead'}\n` +
        `*Contact Person:* ${contactPerson || name || 'N/A'}\n` +
        `*Physical Address:* ${lane.street || 'N/A'}, ${lane.city || ''}, ${lane.state || ''} ${lane.zipCode || ''}\n` +
        `*Services Requested:* ${(lane.serviceTypes || []).join(', ') || 'N/A'}\n` +
        `*Quote Amount:* $${lane.dollarAmount || 0} / ${lane.uom || 'N/A'} (${lane.serviceFrequency || 'N/A'})\n` +
        `*Est. Monthly Value:* $${estMonthlyVal} / mo\n\n` +
        `<${recordLink}|👉 Open Record in Armellini CRM>\n\n` +
        `_Requested by *${requester}* on ${new Date().toLocaleString()}_`
    };

    try {
      await fetch(googleChatWebhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(webhookPayload),
      });
    } catch (chatErr) {
      console.warn('Google Chat Webhook post failed:', chatErr);
    }

    if (accessToken) {
      if (isGoogleTokenExpired()) {
        notifyGoogleTokenExpired('Google access token expired before sending email to Operations.');
        if (showToast) showToast('Google session expired. Please re-authenticate to send via Gmail.');
        return;
      }
      setSendingOpsEmail(true);
      try {
        await sendGmailEmail(
          accessToken,
          'ael-routing@armellini.com',
          subject,
          htmlBody,
          `OPS-${Date.now().toString(36).toUpperCase()}`,
          true
        );
        if (showToast) showToast(`Email & Google Chat message sent to Operations for ${laneHeaderStr}!`, editingLead?.id);
      } catch (err: any) {
        console.warn('Direct Gmail API send failed, falling back to mailto:', err);
        const mailSubject = encodeURIComponent(subject);
        const mailBody = encodeURIComponent(textBody);
        window.open(`mailto:ael-routing@armellini.com?subject=${mailSubject}&body=${mailBody}`, '_blank');
        if (showToast) showToast(`Opened mail client & posted message to Google Chat Space for ${laneHeaderStr}!`, editingLead?.id);
      } finally {
        setSendingOpsEmail(false);
      }
    } else {
      const mailSubject = encodeURIComponent(subject);
      const mailBody = encodeURIComponent(textBody);
      window.open(`mailto:ael-routing@armellini.com?subject=${mailSubject}&body=${mailBody}`, '_blank');
      if (showToast) showToast(`Opened mail client & posted message to Google Chat Space for ${laneHeaderStr}!`, editingLead?.id);
    }
  };

  const handleDuplicateLead = () => {
    if (!editingLead) return;

    const dupCompany = (company || editingLead.company || '').trim();
    const dupName = (name || editingLead.name || '').trim();
    const finalDupCompany = dupCompany ? `${dupCompany} (Copy)` : '';
    const finalDupName = dupName ? `${dupName} (Copy)` : (contactPerson.trim() || 'Copied Lead');

    const duplicatedLanes: LeadLane[] = (lanes && lanes.length > 0 ? lanes : [createBlankLane(1)]).map((l, idx) => ({
      ...l,
      id: `lane-dup-${Date.now()}-${idx + 1}`,
      leadId: '',
      laneNumber: idx + 1,
      approvalStatus: 'pending',
      sentToOpsAt: '',
      sentToOpsBy: '',
      approvedAt: '',
      approvedBy: '',
    }));

    const dupLane1 = duplicatedLanes[0];

    onSave({
      // 1. Lead / Account
      name: finalDupName,
      company: finalDupCompany,
      careOf: (careOf || editingLead.careOf || '').trim(),
      billToAccountNumber: (billToAccountNumber || editingLead.billToAccountNumber || '').trim(),
      leadNotes: leadNotes || editingLead.leadNotes || '',
      // 2. Primary Contact Person
      contactPerson: (contactPerson || editingLead.contactPerson || '').trim(),
      // 3. Phone Number
      phone: (phone || editingLead.phone || '').trim(),
      // 4. Email Address
      email: (email || editingLead.email || '').trim(),
      // 5. Salesperson Ownership
      owner: (owner || editingLead.owner || '').trim(),

      // Default pipeline & status
      pipeline: 'Opportunity',
      status: 'Lead',
      lastContacted: 'Just now',
      location: '',
      accountNumber: (billToAccountNumber || editingLead.billToAccountNumber || editingLead.accountNumber || '').trim(),
      nextSteps: '',
      service: '',
      industry: '',
      appLink: '',
      quoteDetails: '',
      quickNotes: `Duplicated from "${editingLead.company || editingLead.name || 'Lead'}"`,
      followUpDate: '',
      followUpCompletedAt: '',

      // Copy lanes with approvalStatus reset to pending
      lanes: duplicatedLanes,
      dealValue: duplicatedLanes.reduce((sum, l) => sum + (Number(l.estimatedDealValue) || 0), 0),
      quoteDollarAmount: dupLane1?.dollarAmount || 0,
      uom: dupLane1?.uom || undefined,
      handlingFeeAmount: dupLane1?.handlingFee || 0,
      noHandlingFee: Boolean(dupLane1?.noHandlingFee),
      fuelSurcharge: (dupLane1?.fuelSurchargePercent || 0) > 0 && !dupLane1?.noFuelSurcharge,
      fuelSurchargePercent: dupLane1?.fuelSurchargePercent || 0,
      noFuelSurcharge: Boolean(dupLane1?.noFuelSurcharge),
      forecastVolume: dupLane1?.forecastVolume || 1,
      quoteFrequency: dupLane1?.serviceFrequency || undefined,
      minimumMonthlyAmount: dupLane1?.minimumShipmentAmount || 0,
      minimumShipmentAmount: dupLane1?.minimumShipmentAmount || 0,
      paymentTerms: dupLane1?.paymentTerms || '',
      depositAmount: dupLane1?.depositAmount ?? undefined,
      fullQuoteView: dupLane1?.fullQuoteView || '',
      estimatedDealValue: duplicatedLanes.reduce((sum, l) => sum + (Number(l.estimatedDealValue) || 0), 0),

      // Delivery & routing specs (approval reset to pending)
      consigneeName: editingLead.consigneeName || '',
      consigneeAccount: editingLead.consigneeAccount || '',
      shipperName: editingLead.shipperName || '',
      shipperAccount: editingLead.shipperAccount || '',
      street: dupLane1?.street || '',
      city: dupLane1?.city || '',
      state: dupLane1?.state || '',
      zipCode: dupLane1?.zipCode || '',
      fullDeliveryView: dupLane1?.fullDeliveryView || '',
      routingOpsApproved: false,
      quoteRequestedBy: '',
      quoteRequestedAt: '',
      routingApprovedBy: '',
      routingApprovedAt: '',
      leadType: editingLead.leadType ? [...editingLead.leadType] : [],
      serviceTypes: dupLane1?.serviceTypes || [],
      industries: dupLane1?.industries || [],
      outboundStates: dupLane1?.outboundStates || [],
      outboundWarehouses: dupLane1?.outboundWarehouses || [],
      daysOfService: dupLane1?.daysOfService || [],
      inquiryDate: new Date().toISOString().split('T')[0],
      proposalDate: '',
      acceptedDate: '',
      accountSetupDate: '',
      firstShipmentDate: '',
      finalFollowUpDate: '',
      creditAppSentAt: null,
      creditAppSentBy: null,
      rateAgreementSentAt: null,
      rateAgreementSentBy: null,
      internalEmailsSentAt: null,
      internalEmailsSentBy: null,
      firstDeliveryFollowUpAt: null,
      firstDeliveryFollowUpBy: null,
      isTransferred: false,
      isDeclined: false,
      isNotAwarded: false,
      notAwardedReason: ''
    });

    if (showToast) {
      showToast(`Duplicated lead for "${finalDupCompany || finalDupName}"!`);
    }

    onClose();
  };

  // Milestone Dates
  const [inquiryDate, setInquiryDate] = useState('');
  const [proposalDate, setProposalDate] = useState('');
  const [acceptedDate, setAcceptedDate] = useState('');
  const [accountSetupDate, setAccountSetupDate] = useState('');
  const [firstShipmentDate, setFirstShipmentDate] = useState('');
  const [finalFollowUpDate, setFinalFollowUpDate] = useState('');
  const [monthlyRevenue, setMonthlyRevenue] = useState<number>(0);

  // Off-Ramp Disqualifiers
  const [isTransferred, setIsTransferred] = useState(false);
  const [transferredReason, setTransferredReason] = useState<string>('Armellini Air Express');
  const [isDeclined, setIsDeclined] = useState(false);
  const [declinedReason, setDeclinedReason] = useState<string>('Delivery Environment');
  const [isNotAwarded, setIsNotAwarded] = useState(false);
  const [notAwardedReason, setNotAwardedReason] = useState<string>('Rate');
  const [is30DayUnresponsive, setIs30DayUnresponsive] = useState(false);
  const [isArchived, setIsArchived] = useState(false);

  // States for modular quote parsing and UI tabs
  const [quoteText, setQuoteText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [leadDocuments, setLeadDocuments] = useState<LeadDocument[]>([]);
  const [leadActivityLog, setLeadActivityLog] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'files' | 'forms' | 'activity' | 'colors'>('details');
  const [selectedTemplate, setSelectedTemplate] = useState<'outreach' | 'quote' | 'credit' | 'onboard'>('outreach');
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Local state for color customization
  const [localColors, setLocalColors] = useState<Record<string, string>>({});

  // Get unique list of potential salespeople (name + email)
  const salespeople = React.useMemo(() => {
    const defaultList = [
      { name: 'Danean Allan', email: 'danean@armellini.com' },
      { name: 'David Armellini', email: 'darmellini@armellini.com' },
      { name: 'Hilary Dedrick', email: 'hdedrick@armellini.com' },
      { name: 'Fernando Perez', email: 'fperez@armellini.com' },
      { name: 'Sarah Nesbitt', email: 'snesbitt@armellini.com' },
      { name: 'Meybeth Rodriguez', email: 'm_rodriguez@armellini.com' },
    ];
    const dbSalespeople = (users || [])
      .filter(u => u.role === 'sales' || u.role === 'editor' || u.role === 'admin')
      .map(u => ({
        name: u.displayName || u.email.split('@')[0],
        email: u.email
      }));

    const map = new Map<string, { name: string; email: string }>();
    [...defaultList, ...dbSalespeople].forEach(item => {
      if (item.name && !map.has(item.name)) {
        map.set(item.name, item);
      }
    });

    if (owner && !map.has(owner)) {
      map.set(owner, { name: owner, email: owner.includes('@') ? owner : '' });
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [users, owner]);


  // Helper to parse quoteDetails
  const parseQuoteDetails = (raw: string | undefined) => {
    if (!raw) return { text: '', files: [] };
    try {
      if (raw.trim().startsWith('{')) {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null) {
          return {
            text: parsed.text || '',
            files: Array.isArray(parsed.files) ? parsed.files : [],
          };
        }
      }
    } catch (e) {
      // Fail silently
    }
    return { text: raw, files: [] };
  };

  // Populate form if we are editing or have a default pipeline
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details'); // Reset active tab on open
    }

    if (editingLead) {
      setLeadActivityLog(Array.isArray(editingLead.activityLog) ? editingLead.activityLog : []);
      setName(editingLead.name);
      setCompany(editingLead.company);
      setEmail(editingLead.email);
      setDealValue(editingLead.dealValue);
      setPipeline(editingLead.pipeline);
      setStatus(editingLead.status);
      setOwner(editingLead.owner || '');
      setContactPerson(editingLead.contactPerson || '');
      setCareOf(editingLead.careOf || '');
      setLocation(editingLead.location || '');
      setAccountNumber(editingLead.accountNumber || '');
      setBillToAccountNumber(editingLead.billToAccountNumber || editingLead.accountNumber || '');
      setNextSteps(editingLead.nextSteps || '');
      setLeadNotes(editingLead.leadNotes ?? editingLead.nextSteps ?? '');
      setPhone(editingLead.phone || '');
      setService(editingLead.service || '');
      setIndustry(editingLead.industry || '');
      setAppLink(editingLead.appLink || '');
      setFollowUpDate(editingLead.followUpDate || '');
      setFollowUpCompletedAt(editingLead.followUpCompletedAt || '');
      setFollowUpCompleted(Boolean(editingLead.followUpCompleted));
      setFollowUpNotes(editingLead.followUpNotes || '');
      setQuickNotes(editingLead.quickNotes || '');
      setNewNote('');
      
      const parsed = parseQuoteDetails(editingLead.quoteDetails);
      setQuoteDetails(editingLead.quoteDetails || '');
      setQuoteText(parsed.text);
      setAttachedFiles(parsed.files);

      setStreet(editingLead.street || '');
      setCity(editingLead.city || '');
      setState(editingLead.state || '');
      setZipCode(editingLead.zipCode || '');
      setShipperName(editingLead.shipperName || '');
      setShipperAccount(editingLead.shipperAccount || '');
      setConsigneeName(editingLead.consigneeName || '');
      setConsigneeAccount(editingLead.consigneeAccount || '');
      setConsigneeCity(editingLead.consigneeCity || editingLead.city || '');
      setConsigneeState(editingLead.consigneeState || editingLead.state || '');
      setConsigneeZip(editingLead.consigneeZip || editingLead.zipCode || '');
      const isConsigneeMatch = Boolean(
        editingLead.company &&
        editingLead.consigneeName &&
        editingLead.consigneeName.trim().toUpperCase() === editingLead.company.trim().toUpperCase()
      );
      const isShipperMatch = !isConsigneeMatch && Boolean(
        editingLead.company &&
        editingLead.shipperName &&
        editingLead.shipperName.trim().toUpperCase() === editingLead.company.trim().toUpperCase()
      );
      setUseConsigneeAsBillTo(editingLead.billToType ? editingLead.billToType === 'Bill To/Consignee' : isConsigneeMatch);
      setUseShipperAsBillTo(editingLead.billToType ? editingLead.billToType === 'Bill To/Shipper' : isShipperMatch);
      setBillToType(editingLead.billToType || (isShipperMatch ? 'Bill To/Shipper' : 'Bill To/Consignee'));
      setSameAsConsigneeAddress(Boolean(editingLead.location && editingLead.street === editingLead.location));
      setSameAsCompany(Boolean(editingLead.company && editingLead.consigneeName === editingLead.company));
      setRoutingOpsApproved(Boolean(editingLead.routingOpsApproved));

      setLeadType(editingLead.leadType || []);
      setServiceTypes(editingLead.serviceTypes || []);
      setIndustries(editingLead.industries || []);
      setOutboundStates(editingLead.outboundStates || []);
      setOutboundWarehouses(editingLead.outboundWarehouses || []);
      setDaysOfService(editingLead.daysOfService || []);

      setQuoteDollarAmount(editingLead.quoteDollarAmount || 0);
      setUom(editingLead.uom || 'Cube');
      setHandlingFeeAmount(editingLead.handlingFeeAmount || 0);
      setNoHandlingFee(Boolean(editingLead.noHandlingFee));
      setFuelSurcharge(Boolean(editingLead.fuelSurcharge));
      setFuelSurchargePercent(editingLead.fuelSurchargePercent || 0);
      setNoFuelSurcharge(Boolean(editingLead.noFuelSurcharge));
      setForecastVolume(editingLead.forecastVolume || 1);
      setQuoteFrequency(editingLead.quoteFrequency || 'Weekly');
      setMinimumMonthlyAmount(editingLead.minimumMonthlyAmount || editingLead.minimumShipmentAmount || 0);
      setMinimumShipmentAmount(editingLead.minimumShipmentAmount || editingLead.minimumMonthlyAmount || 0);
      const validTerms = ['Net 7 w/ Deposit', 'Net 7', 'Net 14', 'Net 30'];
      setPaymentTerms(editingLead.paymentTerms && validTerms.includes(editingLead.paymentTerms) ? editingLead.paymentTerms : '');
      setDepositAmount(editingLead.depositAmount || 0);

      if (Array.isArray(editingLead.lanes) && editingLead.lanes.length > 0) {
        setLanes(editingLead.lanes.map((l, idx) => ({
          ...createBlankLane(idx + 1),
          ...l,
          laneNumber: idx + 1,
          paymentTerms: l.paymentTerms && validTerms.includes(l.paymentTerms) ? l.paymentTerms : '',
        })));
      } else {
        setLanes([{
          id: `lane-${editingLead.id}-1`,
          leadId: editingLead.id,
          laneNumber: 1,
          street: editingLead.street || '',
          city: editingLead.city || '',
          state: editingLead.state || '',
          zipCode: editingLead.zipCode || '',
          sameAsConsignee: Boolean(editingLead.location && editingLead.street === editingLead.location),
          shipperName: editingLead.shipperName || '',
          consigneeName: editingLead.consigneeName || '',
          rateAgreementRole: editingLead.billToType || (isShipperMatch ? 'Bill To/Shipper' : 'Bill To/Consignee'),
          consigneeCity: editingLead.consigneeCity || editingLead.city || '',
          consigneeState: editingLead.consigneeState || editingLead.state || '',
          consigneeZip: editingLead.consigneeZip || editingLead.zipCode || '',
          fullDeliveryView: editingLead.fullDeliveryView || '',
          serviceTypes: editingLead.serviceTypes || [],
          industries: editingLead.industries || [],
          outboundStates: editingLead.outboundStates || [],
          outboundWarehouses: editingLead.outboundWarehouses || [],
          daysOfService: editingLead.daysOfService || [],
          dollarAmount: editingLead.quoteDollarAmount || 0,
          uom: editingLead.uom || 'Cube',
          handlingFee: editingLead.handlingFeeAmount || 0,
          noHandlingFee: Boolean(editingLead.noHandlingFee),
          fuelSurchargePercent: editingLead.fuelSurchargePercent || 0,
          noFuelSurcharge: Boolean(editingLead.noFuelSurcharge),
          forecastVolume: editingLead.forecastVolume || 1,
          serviceFrequency: editingLead.quoteFrequency || 'Weekly',
          minimumShipmentAmount: editingLead.minimumShipmentAmount || editingLead.minimumMonthlyAmount || 0,
          paymentTerms: editingLead.paymentTerms && validTerms.includes(editingLead.paymentTerms) ? editingLead.paymentTerms : '',
          depositAmount: editingLead.depositAmount || 0,
          fullQuoteView: editingLead.fullQuoteView || '',
          estimatedDealValue: editingLead.estimatedDealValue || 0,
          approvalStatus: editingLead.routingOpsApproved ? 'approved' : (editingLead.quoteRequestedAt ? 'sent' : 'pending'),
          sentToOpsAt: editingLead.quoteRequestedAt || '',
          sentToOpsBy: editingLead.quoteRequestedBy || '',
          approvedAt: editingLead.routingApprovedAt || '',
          approvedBy: editingLead.routingApprovedBy || '',
        }]);
      }

      setQuoteRequestedBy(editingLead.quoteRequestedBy || '');
      setQuoteRequestedAt(editingLead.quoteRequestedAt || '');
      setRoutingApprovedBy(editingLead.routingApprovedBy || '');
      setRoutingApprovedAt(editingLead.routingApprovedAt || '');

      setInquiryDate(editingLead.inquiryDate || '');
      setProposalDate(editingLead.proposalDate || '');
      setAcceptedDate(editingLead.acceptedDate || '');
      setAccountSetupDate(editingLead.accountSetupDate || '');
      setFirstShipmentDate(editingLead.firstShipmentDate || '');
      setFinalFollowUpDate(editingLead.finalFollowUpDate || '');
      setMonthlyRevenue(editingLead.monthlyRevenue || 0);

      setCreditAppSentAt(editingLead.creditAppSentAt || '');
      setCreditAppSentBy(editingLead.creditAppSentBy || '');
      setRateAgreementSentAt(editingLead.rateAgreementSentAt || '');
      setRateAgreementSentBy(editingLead.rateAgreementSentBy || '');
      setInternalEmailsSentAt(editingLead.internalEmailsSentAt || '');
      setInternalEmailsSentBy(editingLead.internalEmailsSentBy || '');
      setFirstDeliveryFollowUpAt(editingLead.firstDeliveryFollowUpAt || '');
      setFirstDeliveryFollowUpBy(editingLead.firstDeliveryFollowUpBy || '');

      setIsTransferred(Boolean(editingLead.isTransferred));
      setTransferredReason(editingLead.transferredReason || 'Armellini Air Express');
      setIsDeclined(Boolean(editingLead.isDeclined));
      setDeclinedReason(editingLead.declinedReason || 'Delivery Environment');
      setIsNotAwarded(Boolean(editingLead.isNotAwarded));
      setNotAwardedReason(editingLead.notAwardedReason || 'Rate');
      setIs30DayUnresponsive(Boolean(editingLead.is30DayUnresponsive));
      setIsArchived(Boolean(editingLead.isArchived));
    } else {
      setLeadActivityLog([]);
      setName('');
      setCompany('');
      setEmail('');
      setDealValue(0);
      setPipeline(defaultPipeline || 'Opportunity');
      setStatus('Lead');

      const currentEmail = auth.currentUser?.email?.toLowerCase() || '';
      const matchedUser = (users || []).find(u => u.email.toLowerCase() === currentEmail);
      const matchedDefault = [
        { name: 'Meybeth Rodriguez', email: 'm_rodriguez@armellini.com' },
        { name: 'Danean Allan', email: 'danean@armellini.com' },
        { name: 'Sarah Nesbitt', email: 'snesbitt@armellini.com' },
        { name: 'Fernando Perez', email: 'fperez@armellini.com' },
        { name: 'David Armellini', email: 'darmellini@armellini.com' },
        { name: 'David Armellini', email: 'darmellini@armellini.com' },
        { name: 'Hilary Dedrick', email: 'hdedrick@armellini.com' },
      ].find(d => d.email.toLowerCase() === currentEmail);

      const resolvedOwnerName = matchedUser?.displayName || matchedDefault?.name || (
        auth.currentUser?.displayName && auth.currentUser.displayName !== 'Mark Sterling'
          ? auth.currentUser.displayName
          : 'Danean Allan'
      );
      setOwner(resolvedOwnerName);
      setContactPerson('');
      setCareOf('');
      setLocation('');
      setAccountNumber('');
      setBillToAccountNumber('');
      setNextSteps('');
      setLeadNotes('');
      setPhone('');
      setService('');
      setIndustry('');
      setAppLink('');
      setQuoteDetails('');
      setQuoteText('');
      setAttachedFiles([]);
      const todayDateEntered = new Date().toISOString().slice(0, 10);
      setFollowUpDate(todayDateEntered);
      setFollowUpCompletedAt('');
      setFollowUpCompleted(false);
      setFollowUpNotes('');
      setQuickNotes('');
      setNewNote('');

      setStreet('');
      setCity('');
      setState('');
      setZipCode('');
      setShipperName('');
      setShipperAccount('');
      setConsigneeName('');
      setConsigneeAccount('');
      setConsigneeCity('');
      setConsigneeState('');
      setConsigneeZip('');
      setBillToType('Bill To/Consignee');
      setUseConsigneeAsBillTo(false);
      setUseShipperAsBillTo(false);
      setSameAsConsigneeAddress(false);
      setSameAsCompany(false);
      setRoutingOpsApproved(false);

      setLeadType([]);
      setServiceTypes([]);
      setIndustries([]);
      setOutboundStates([]);
      setOutboundWarehouses([]);
      setDaysOfService([]);

      setQuoteDollarAmount(0);
      setUom('Cube');
      setHandlingFeeAmount(0);
      setNoHandlingFee(false);
      setFuelSurcharge(false);
      setQuoteFrequency('Weekly');
      setMinimumMonthlyAmount(0);
      setMinimumShipmentAmount(0);
      setPaymentTerms('');
      setDepositAmount(0);
      setLanes([createBlankLane(1)]);

      setQuoteRequestedBy('');
      setQuoteRequestedAt('');
      setRoutingApprovedBy('');
      setRoutingApprovedAt('');

      setInquiryDate(new Date().toISOString().slice(0, 10));
      setProposalDate('');
      setAcceptedDate('');
      setAccountSetupDate('');
      setFirstShipmentDate('');
      setFinalFollowUpDate('');
      setMonthlyRevenue(0);

      setCreditAppSentAt('');
      setCreditAppSentBy('');
      setRateAgreementSentAt('');
      setRateAgreementSentBy('');
      setInternalEmailsSentAt('');
      setInternalEmailsSentBy('');
      setFirstDeliveryFollowUpAt('');
      setFirstDeliveryFollowUpBy('');

      setIsTransferred(false);
      setTransferredReason('Armellini Air Express');
      setIsDeclined(false);
      setDeclinedReason('Delivery Environment');
      setIsNotAwarded(false);
      setNotAwardedReason('Rate');
      setIs30DayUnresponsive(false);
      setIsArchived(false);
    }
  }, [editingLead, defaultPipeline, isOpen]);

  // Keep local colors synchronized with the parent statusColors state when opened
  useEffect(() => {
    if (statusColors) {
      setLocalColors(statusColors);
    }
  }, [statusColors, isOpen]);

  // Text Selection formatting helper for Quick Notes
  const handleFormatText = (type: 'bold' | 'italic' | 'bullet' | 'time' | 'highlight') => {
    const textarea = document.getElementById('quickNotes') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selected = text.substring(start, end);

    let replacement = '';
    let offsetCursor = 0;

    switch (type) {
      case 'bold':
        replacement = `**${selected || 'bold text'}**`;
        offsetCursor = selected ? 0 : 2;
        break;
      case 'italic':
        replacement = `*${selected || 'italic text'}*`;
        offsetCursor = selected ? 0 : 1;
        break;
      case 'bullet':
        replacement = selected
          ? selected.split('\n').map(line => line.startsWith('• ') ? line : `• ${line}`).join('\n')
          : '• ';
        break;
      case 'time':
        const nowStr = `[${new Date().toLocaleString()}] `;
        replacement = nowStr + selected;
        break;
      case 'highlight':
        replacement = `==${selected || 'highlighted text'}==`;
        offsetCursor = selected ? 0 : 2;
        break;
      default:
        break;
    }

    const newVal = text.substring(0, start) + replacement + text.substring(end);
    setNewNote(newVal);

    // Re-focus and set selection back
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + replacement.length - offsetCursor;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleColorChange = (statusVal: string, colorHex: string) => {
    const updated = { ...localColors, [statusVal]: colorHex };
    setLocalColors(updated);
    onSaveStatusColors(updated);
  };

  const handleResetColors = () => {
    const defaultColors: Record<string, string> = { ...DEFAULT_STATUS_COLORS };
    setLocalColors(defaultColors);
    onSaveStatusColors(defaultColors);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalCompany = company.trim();
    if (!finalCompany) {
      if (showToast) {
        showToast('Company Name is required to save a lead.');
      }
      return;
    }

    const finalName = name.trim() || contactPerson.trim() || `${finalCompany} Contact`;

    const activeLanes = lanes.length > 0 ? lanes : [createBlankLane(1)];
    for (let i = 0; i < activeLanes.length; i++) {
      const l = activeLanes[i];
      if (l.paymentTerms === 'Net 7 w/ Deposit' && (!l.depositAmount || Number(l.depositAmount) <= 0)) {
        if (showToast) {
          showToast(`Lane ${i + 1}: Deposit Amount ($) is required when Payment Terms is "Net 7 w/ Deposit".`);
        }
        return;
      }
    }

    const computedLanes: LeadLane[] = activeLanes.map((l, idx) => {
      const laneDeliveryView = formatFullDeliveryView(l.street || '', l.city || '', l.state || '', l.zipCode || '');
      const hasFsc = (l.fuelSurchargePercent || 0) > 0 && !l.noFuelSurcharge;
      const laneQuoteView = formatFullQuoteView(
        l.dollarAmount || 0, l.uom || '', l.handlingFee || 0, Boolean(l.noHandlingFee), hasFsc,
        l.serviceFrequency || '', l.forecastVolume || 1, l.fuelSurchargePercent || 0, Boolean(l.noFuelSurcharge),
        l.paymentTerms, l.depositAmount ?? undefined
      );
      const laneEstValue = calculateEstimatedDealValue(
        l.dollarAmount || 0, l.uom || '', l.handlingFee || 0, Boolean(l.noHandlingFee), hasFsc,
        l.serviceFrequency || '', l.minimumShipmentAmount || 0, l.forecastVolume || 1, l.fuelSurchargePercent || 0, Boolean(l.noFuelSurcharge)
      );
      return {
        ...l,
        laneNumber: idx + 1,
        fullDeliveryView: laneDeliveryView,
        fullQuoteView: laneQuoteView,
        estimatedDealValue: laneEstValue,
      };
    });

    const lane1 = computedLanes[0];
    const calculatedDeliveryView = lane1.fullDeliveryView;
    const calculatedQuoteView = lane1.fullQuoteView;
    const calculatedEstValue = computedLanes.reduce((sum, l) => sum + (Number(l.estimatedDealValue) || 0), 0);

    const calculatedState = calculatePipelineState({
      inquiryDate, proposalDate, acceptedDate, firstShipmentDate, finalFollowUpDate,
      isTransferred, isDeclined, isNotAwarded, is30DayUnresponsive
    });

    const currentUser = auth.currentUser?.displayName || auth.currentUser?.email || 'Sales Representative';
    let currentActivityLog = leadActivityLog.length > 0
      ? [...leadActivityLog]
      : (editingLead?.activityLog ? [...editingLead.activityLog] : []);
    let saveFollowUpDate = followUpDate;
    let saveFollowUpNotes = followUpNotes;
    let saveFollowUpCompletedAt = followUpCompletedAt;
    let saveLastContacted = editingLead ? editingLead.lastContacted : 'Just now';

    if (followUpCompleted) {
      const nowIso = new Date().toISOString();
      const completionEntry = {
        id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        type: 'followup_completed',
        action: 'Follow-up Completed',
        timestamp: nowIso,
        details: `Completed follow-up scheduled for ${followUpDate || 'N/A'}.${followUpNotes.trim() ? ` Notes: ${followUpNotes.trim()}` : ''}`,
        user: currentUser,
      };
      currentActivityLog = [completionEntry, ...currentActivityLog];
      saveFollowUpCompletedAt = nowIso;
      saveLastContacted = 'Just now';
      // Automatically reset followup_completed back to false and clear followup_date/notes for next scheduled touchpoint
      saveFollowUpDate = '';
      saveFollowUpNotes = '';
    } else if (saveFollowUpDate && saveFollowUpDate !== (editingLead?.followUpDate || '')) {
      // A newly scheduled or rescheduled follow-up date starts fresh (not completed)
      saveFollowUpCompletedAt = '';
    }

    const formatToMmDdYyyy = (isoDate: string) => {
      const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[2]}/${m[3]}/${m[1]}`;
      const now = new Date();
      return `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;
    };

    const checkboxNotes: string[] = [];
    const onboardingChecks = [
      { label: 'Credit App Sent', prev: editingLead?.creditAppSentAt || '', next: creditAppSentAt, user: creditAppSentBy || currentUser },
      { label: 'Rate Agreement Sent', prev: editingLead?.rateAgreementSentAt || '', next: rateAgreementSentAt, user: rateAgreementSentBy || currentUser },
      { label: 'Internal Emails Sent', prev: editingLead?.internalEmailsSentAt || '', next: internalEmailsSentAt, user: internalEmailsSentBy || currentUser },
      { label: 'First Delivery Follow Up', prev: editingLead?.firstDeliveryFollowUpAt || '', next: firstDeliveryFollowUpAt, user: firstDeliveryFollowUpBy || currentUser },
    ];
    for (const chk of onboardingChecks) {
      if (!chk.prev && chk.next) {
        checkboxNotes.push(`${formatToMmDdYyyy(chk.next)} — ${chk.label} — ${chk.user}`);
      } else if (chk.prev && !chk.next) {
        const now = new Date();
        const todayStr = `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;
        checkboxNotes.push(`${todayStr} — ${chk.label} unchecked — ${currentUser}`);
      }
    }

    let finalQuickNotes = quickNotes;
    if (checkboxNotes.length > 0) {
      finalQuickNotes = `${checkboxNotes.join('\n')}${finalQuickNotes.trim() ? `\n\n${finalQuickNotes.trim()}` : ''}`;
    }
    if (newNote.trim()) {
      finalQuickNotes = `📅 ${new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })} — ${currentUser}\n${newNote.trim()}${finalQuickNotes.trim() ? `\n\n${finalQuickNotes.trim()}` : ''}`;
    }

    onSave({
      id: editingLead?.id,
      name: finalName,
      company: finalCompany,
      email,
      dealValue: calculatedEstValue || dealValue || 0,
      pipeline: calculatedState.pipeline || pipeline,
      status: calculatedState.status || status,
      lastContacted: saveLastContacted,
      owner,
      contactPerson: contactPerson || (finalName !== `${finalCompany} Contact` ? finalName : ''),
      careOf,
      location,
      accountNumber: billToAccountNumber || accountNumber,
      billToAccountNumber,
      nextSteps: leadNotes,
      leadNotes,
      phone,
      service: (lane1.serviceTypes || []).join(', ') || service,
      industry: (lane1.industries || []).join(', ') || industry,
      appLink,
      quoteDetails: JSON.stringify({
        text: quoteText || calculatedQuoteView,
        files: attachedFiles,
      }),
      followUpDate: saveFollowUpDate,
      followUpCompletedAt: saveFollowUpCompletedAt,
      followUpCompleted: false, // Reset to false for next activity cycle
      followUpNotes: saveFollowUpNotes,
      activityLog: currentActivityLog,
      quickNotes: finalQuickNotes,

      // Automation Engine Fields (Synced from Lane 1 + Multi-Lane)
      billToType: lane1.rateAgreementRole || billToType,
      street: lane1.street, city: lane1.city, state: lane1.state, zipCode: lane1.zipCode,
      consigneeName: lane1.consigneeName || consigneeName, consigneeAccount,
      consigneeCity: lane1.city || consigneeCity, consigneeState: lane1.state || consigneeState, consigneeZip: lane1.zipCode || consigneeZip,
      shipperName: lane1.shipperName || shipperName, shipperAccount,
      fullDeliveryView: calculatedDeliveryView,
      routingOpsApproved: lane1.approvalStatus === 'approved',
      quoteRequestedBy: lane1.sentToOpsBy || quoteRequestedBy,
      quoteRequestedAt: lane1.sentToOpsAt || quoteRequestedAt,
      routingApprovedBy: lane1.approvedBy || routingApprovedBy,
      routingApprovedAt: lane1.approvedAt || routingApprovedAt,
      leadType,
      serviceTypes: lane1.serviceTypes,
      industries: lane1.industries,
      outboundStates: lane1.outboundStates,
      outboundWarehouses: lane1.outboundWarehouses,
      daysOfService: lane1.daysOfService,
      quoteDollarAmount: lane1.dollarAmount,
      uom: lane1.uom || undefined,
      handlingFeeAmount: lane1.handlingFee,
      noHandlingFee: lane1.noHandlingFee,
      fuelSurcharge: (lane1.fuelSurchargePercent || 0) > 0 && !lane1.noFuelSurcharge,
      fuelSurchargePercent: lane1.fuelSurchargePercent,
      noFuelSurcharge: lane1.noFuelSurcharge,
      forecastVolume: lane1.forecastVolume,
      quoteFrequency: lane1.serviceFrequency || undefined,
      minimumMonthlyAmount: lane1.minimumShipmentAmount || minimumMonthlyAmount,
      minimumShipmentAmount: lane1.minimumShipmentAmount,
      paymentTerms: lane1.paymentTerms,
      depositAmount: lane1.paymentTerms === 'Net 7 w/ Deposit' ? (lane1.depositAmount ?? undefined) : undefined,
      lanes: computedLanes,
      fullQuoteView: calculatedQuoteView,
      estimatedDealValue: calculatedEstValue,
      inquiryDate, proposalDate, acceptedDate, accountSetupDate, firstShipmentDate,
      finalFollowUpDate, monthlyRevenue,
      creditAppSentAt: creditAppSentAt || undefined,
      creditAppSentBy: creditAppSentBy || undefined,
      rateAgreementSentAt: rateAgreementSentAt || undefined,
      rateAgreementSentBy: rateAgreementSentBy || undefined,
      internalEmailsSentAt: internalEmailsSentAt || undefined,
      internalEmailsSentBy: internalEmailsSentBy || undefined,
      firstDeliveryFollowUpAt: firstDeliveryFollowUpAt || undefined,
      firstDeliveryFollowUpBy: firstDeliveryFollowUpBy || undefined,
      isTransferred, transferredReason,
      isDeclined, declinedReason,
      isNotAwarded, notAwardedReason,
      is30DayUnresponsive,
      isArchived: isArchived || is30DayUnresponsive
    });

    if (saveFollowUpDate && syncToCalendar && !followUpCompleted && accessToken) {
      const ownerObj = users.find(u => u.displayName === owner || u.email === owner);
      const targetOwnerEmail = ownerObj?.email || (owner && owner.includes('@') ? owner : undefined);
      createFollowUpEvent(
        accessToken,
        finalName,
        finalCompany,
        saveFollowUpDate,
        targetOwnerEmail,
        saveFollowUpNotes
      ).then(() => {
        if (showToast) showToast(`Google Calendar event & email invite created for ${targetOwnerEmail || owner || 'Lead Owner'}!`, editingLead?.id);
      }).catch((err) => {
        console.warn('Google Calendar sync error:', err);
        if (showToast) showToast(`Calendar note: ${err.message || 'Could not sync event'}`, editingLead?.id);
      });
    }

    onClose();
  };

  // Load CRM-managed lead documents and reconciled activity log from PostgreSQL/storage
  const loadLeadDocuments = React.useCallback(async () => {
    if (!editingLead?.id) {
      setLeadDocuments([]);
      return;
    }
    try {
      setLoadingDocuments(true);
      const res = await authedFetch(`/api/leads/${editingLead.id}/forms`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.documents)) {
          setLeadDocuments(data.documents);
        }
        if (Array.isArray(data.activityLog)) {
          setLeadActivityLog(data.activityLog);
        }
      } else {
        const docRes = await authedFetch(`/api/leads/${editingLead.id}/documents`);
        if (docRes.ok) {
          const docs = await docRes.json();
          setLeadDocuments(docs || []);
        }
      }
    } catch (err) {
      console.error('Failed to load lead documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  }, [editingLead?.id]);

  useEffect(() => {
    if (isOpen && editingLead?.id) {
      loadLeadDocuments();
    } else {
      setLeadDocuments([]);
    }
  }, [isOpen, editingLead?.id, loadLeadDocuments]);

  useEffect(() => {
    if (isOpen && editingLead?.id && activeTab === 'activity') {
      loadLeadDocuments();
    }
  }, [isOpen, editingLead?.id, activeTab, loadLeadDocuments]);

  const uploadFiles = async (files: FileList | File[]) => {
    const fileList = Array.from(files);
    if (!fileList.length) return;

    // Track file names in attachedFiles state
    const newFileNames = fileList.map((f) => f.name);
    setAttachedFiles((prev) => [...prev, ...newFileNames]);

    if (!editingLead?.id) return;

    setUploadingDocument(true);
    for (const file of fileList) {
      try {
        const base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const res = await authedFetch(`/api/leads/${editingLead.id}/documents/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentType: 'attachment',
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            fileData: base64Data,
            fileSize: file.size,
          }),
        });

        if (res.ok) {
          if (showToast) showToast(`Uploaded ${file.name} to CRM documents.`, editingLead?.id);
        }
      } catch (err: any) {
        console.error('Error uploading file:', err);
        if (showToast) showToast(`Failed to upload ${file.name}: ${err.message}`, editingLead?.id);
      }
    }
    setUploadingDocument(false);
    await loadLeadDocuments();
  };

  const handleDeleteLeadDocument = async (docId: string, docName: string) => {
    if (!confirm(`Delete document "${docName}" from lead records?`)) return;
    try {
      const res = await authedFetch(`/api/leads/${editingLead?.id}/documents/${docId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        if (showToast) showToast('Document deleted.', editingLead?.id);
        await loadLeadDocuments();
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  // Drag and Drop files helpers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFiles(e.target.files);
    }
  };

  const removeFile = (indexToRemove: number) => {
    setAttachedFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
          />

          {/* Centered Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-3 md:p-8 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 18 }}
              transition={{ type: 'spring', damping: 26, stiffness: 300 }}
              className="pointer-events-auto w-full max-w-4xl max-h-[92vh] flex"
            >
              <div className="flex w-full max-h-[92vh] flex-col bg-white dark:bg-slate-950 shadow-2xl border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 flex items-center justify-between shrink-0">
                  <div>
                    <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">
                      {editingLead ? 'Edit CRM Lead Details' : 'Create New CRM Lead'}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {editingLead
                        ? 'Update details to synchronize back with the CRM pipeline.'
                        : 'Fill in the data fields below to place onto the board.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    {editingLead && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const nextArchived = !isArchived;
                            setIsArchived(nextArchived);
                            if (nextArchived === false) {
                              setIs30DayUnresponsive(false);
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer border shadow-sm ${
                            isArchived
                              ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 border-emerald-300 dark:border-emerald-800'
                              : 'text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 border-slate-300 dark:border-slate-700'
                          }`}
                          title={isArchived ? "Unarchive lead" : "Archive lead"}
                        >
                          <Archive className={`w-3.5 h-3.5 ${isArchived ? 'text-emerald-600' : 'text-purple-600'}`} />
                          {isArchived ? 'Unarchive Lead' : 'Archive Lead'}
                        </button>
                        <button
                          type="button"
                          onClick={handleDuplicateLead}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer border border-slate-300 dark:border-slate-700 shadow-sm"
                          title="Duplicate lead with contact details only (no rate info)"
                        >
                          <Copy className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                          Duplicate Lead
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 p-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
                    >
                      <span className="sr-only">Close panel</span>
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Tabs Selector */}
                <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 px-6 shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveTab('details')}
                    className={`flex items-center gap-1.5 py-3 px-1 text-xs font-bold border-b-2 transition cursor-pointer ${
                      activeTab === 'details'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'
                    }`}
                  >
                    <Briefcase className="w-3.5 h-3.5" /> Lead Fields
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('files')}
                    className={`flex items-center gap-1.5 py-3 px-1 ml-6 text-xs font-bold border-b-2 transition cursor-pointer ${
                      activeTab === 'files'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'
                    }`}
                  >
                    <Paperclip className="w-3.5 h-3.5" /> Attachments
                    {(attachedFiles.length > 0 || leadDocuments.length > 0) && (
                      <span className="bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-mono text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                        {attachedFiles.length + leadDocuments.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('forms')}
                    className={`flex items-center gap-1.5 py-3 px-1 ml-6 text-xs font-bold border-b-2 transition cursor-pointer ${
                      activeTab === 'forms'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'
                    }`}
                  >
                    <FileSignature className="w-3.5 h-3.5" /> Online Forms
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('activity')}
                    className={`flex items-center gap-1.5 py-3 px-1 ml-6 text-xs font-bold border-b-2 transition cursor-pointer ${
                      activeTab === 'activity'
                        ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100'
                    }`}
                  >
                    <Activity className="w-3.5 h-3.5" /> Activity History
                    {editingLead?.activityLog && editingLead.activityLog.length > 0 && (
                      <span className="bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono text-[10px] px-1.5 py-0.5 rounded-full ml-1">
                        {editingLead.activityLog.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Form Body */}
                <div className="flex-1 px-6 py-6 overflow-y-auto">
                  <form id="lead-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                    
                    {/* TAB 1: Lead Details Fields */}
                    {activeTab === 'details' && (
                      <div className="flex flex-col gap-5">
                        {/* Contact Information Section */}
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-4 mb-5">
                          <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                            <UserCheck className="w-4 h-4" /> Contact Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Row 1, Left: Name Of Person Calling */}
                            <div>
                              <label htmlFor="lead_name" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                Name Of Person Calling <span className="text-rose-500">*</span>
                              </label>
                              <input
                                type="text"
                                id="lead_name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                placeholder="Enter the name of the person that is calling"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 font-semibold"
                              />
                            </div>

                            {/* Row 1, Right: Care Of (if applicable) */}
                            <div>
                              <label htmlFor="care_of" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                Care Of (if applicable)
                              </label>
                              <input
                                type="text"
                                id="care_of"
                                value={careOf}
                                onChange={(e) => setCareOf(e.target.value)}
                                placeholder="Optional — e.g. Parent Co. or Broker Name"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 font-semibold"
                              />
                            </div>

                            {/* Row 2, Left: BILL-TO / COMPANY NAME */}
                            <div>
                              <label htmlFor="company" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                BILL-TO / COMPANY NAME <span className="text-rose-500">*</span>
                                <Tooltip text="Bill-To / Company Name. Formatted in ALL CAPS." />
                              </label>
                              <input
                                type="text"
                                id="company"
                                value={company}
                                onChange={(e) => handleCompanyInputChange(e.target.value)}
                                required
                                placeholder="ENTER BILL-TO / COMPANY NAME"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-semibold"
                                autoComplete="off"
                              />
                            </div>

                            {/* Row 2, Right: BILL-To Account Number */}
                            <div>
                              <label htmlFor="bill_to_account_number" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                BILL-To Account Number
                              </label>
                              <input
                                type="text"
                                id="bill_to_account_number"
                                value={billToAccountNumber}
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  setBillToAccountNumber(val);
                                  setAccountNumber(val);
                                }}
                                placeholder="e.g. B123456"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-mono"
                              />
                            </div>

                            {/* Row 3, Left: CONSIGNEE NAME */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between mb-1.5">
                                <label htmlFor="consignee_name" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                  CONSIGNEE NAME
                                </label>
                                <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={useConsigneeAsBillTo}
                                    onChange={(e) => handleToggleUseConsigneeAsBillTo(e.target.checked)}
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                  <span>Use as Bill-To / Company Name</span>
                                </label>
                              </div>
                              <input
                                type="text"
                                id="consignee_name"
                                value={consigneeName}
                                onChange={(e) => handleConsigneeNameChange(e.target.value)}
                                onFocus={() => { if (consigneeSuggestions.length > 0) setShowConsigneeDropdown(true); }}
                                placeholder="START TYPING CONSIGNEE NAME"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-semibold"
                                autoComplete="off"
                              />

                              {/* Consignee Suggestions Dropdown */}
                              {showConsigneeDropdown && consigneeSuggestions.length > 0 && (
                                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-slate-100 dark:divide-slate-800">
                                  {consigneeSuggestions.map((item, idx) => (
                                    <button
                                      key={`consignee-${item.consigneeAccount || idx}-${idx}`}
                                      type="button"
                                      onClick={() => handleSelectConsignee(item)}
                                      className="w-full text-left px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-xs transition cursor-pointer flex items-center justify-between gap-2"
                                    >
                                      <div className="min-w-0">
                                        <div className="font-bold text-slate-800 dark:text-slate-200 uppercase truncate">
                                          {item.consigneeName}
                                        </div>
                                        {(item.consigneeAddress || item.consigneeCity) && (
                                          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                                            {[item.consigneeAddress, item.consigneeCity, item.consigneeState].filter(Boolean).join(', ')}
                                          </div>
                                        )}
                                      </div>
                                      {item.consigneeAccount && (
                                        <span className="font-mono text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300 px-1.5 py-0.5 rounded shrink-0">
                                          #{item.consigneeAccount}
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Row 3, Right: CONSIGNEE ACCOUNT NUMBER */}
                            <div>
                              <label htmlFor="consignee_account" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                CONSIGNEE ACCOUNT NUMBER
                              </label>
                              <input
                                type="text"
                                id="consignee_account"
                                value={consigneeAccount}
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  setConsigneeAccount(val);
                                  if (useConsigneeAsBillTo) {
                                    setBillToAccountNumber(val);
                                    setAccountNumber(val);
                                  }
                                }}
                                placeholder="e.g. C123456"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-mono"
                              />
                            </div>

                            {/* Row 4, Left: SHIPPER NAME */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between mb-1.5">
                                <label htmlFor="shipper_name" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                  SHIPPER NAME
                                </label>
                                <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={useShipperAsBillTo}
                                    onChange={(e) => handleToggleUseShipperAsBillTo(e.target.checked)}
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                  <span>Use as Bill-To / Company Name</span>
                                </label>
                              </div>
                              <input
                                type="text"
                                id="shipper_name"
                                value={shipperName}
                                onChange={(e) => handleShipperNameChange(e.target.value)}
                                onFocus={() => { if (shipperSuggestions.length > 0) setShowShipperDropdown(true); }}
                                placeholder="START TYPING SHIPPER NAME"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-semibold"
                                autoComplete="off"
                              />

                              {/* Shipper Suggestions Dropdown */}
                              {showShipperDropdown && shipperSuggestions.length > 0 && (
                                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-slate-100 dark:divide-slate-800">
                                  {shipperSuggestions.map((item, idx) => (
                                    <button
                                      key={`shipper-${item.shipperAccount || item.a4shipid || idx}-${idx}`}
                                      type="button"
                                      onClick={() => handleSelectShipper(item)}
                                      className="w-full text-left px-4 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-xs transition cursor-pointer flex items-center justify-between gap-2"
                                    >
                                      <div className="min-w-0">
                                        <div className="font-bold text-slate-800 dark:text-slate-200 uppercase truncate">
                                          {item.shipperName || item.shippername}
                                        </div>
                                        {(item.shipperAddress || item.shipperCity || item.city) && (
                                          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                                            {item.shipperAddress || [item.shipperCity || item.city, item.shipperState || item.state].filter(Boolean).join(', ')}
                                          </div>
                                        )}
                                      </div>
                                      {(item.shipperAccount || item.a4shipid || item.shipper_account) && (
                                        <span className="font-mono text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-1.5 py-0.5 rounded shrink-0">
                                          #{item.shipperAccount || item.a4shipid || item.shipper_account}
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Row 4, Right: SHIPPER ACCOUNT NUMBER */}
                            <div>
                              <label htmlFor="shipper_account" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                SHIPPER ACCOUNT NUMBER
                              </label>
                              <input
                                type="text"
                                id="shipper_account"
                                value={shipperAccount}
                                onChange={(e) => {
                                  const val = e.target.value.toUpperCase();
                                  setShipperAccount(val);
                                  if (useShipperAsBillTo) {
                                    setBillToAccountNumber(val);
                                    setAccountNumber(val);
                                  }
                                }}
                                placeholder="e.g. S123456"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 uppercase font-mono"
                              />
                            </div>

                            {/* Row 4, Left: CONTACT EMAIL ADDRESS */}
                            <div>
                              <label htmlFor="email" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                CONTACT EMAIL ADDRESS
                              </label>
                              <div className="relative">
                                <Mail className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                                <input
                                  type="email"
                                  id="email"
                                  value={email ?? ''}
                                  onChange={(e) => setEmail(e.target.value)}
                                  placeholder="e.g. contact@company.com"
                                  className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pl-9 pr-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400"
                                />
                              </div>
                            </div>

                            {/* Row 4, Right: CONTACT PHONE NUMBER */}
                            <div>
                              <label htmlFor="phone" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                CONTACT PHONE NUMBER
                              </label>
                              <div className="relative">
                                <Phone className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                                <input
                                  type="text"
                                  id="phone"
                                  value={phone ?? ''}
                                  onChange={(e) => setPhone(e.target.value)}
                                  placeholder="e.g. (555) 123-4567"
                                  className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pl-9 pr-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400"
                                />
                              </div>
                            </div>

                            {/* Row 5: Lead Type (Categorization Only) */}
                            <div className="md:col-span-2">
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                  Lead Type <span className="text-rose-500">*</span>
                                </label>
                                <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">
                                  Categorization linked to dashboard column; does not affect pipeline triggers
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-6 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                                {LEAD_TYPES.map((typeOption) => {
                                  const isChecked = leadType.includes(typeOption);
                                  return (
                                    <label
                                      key={typeOption}
                                      className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer select-none hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setLeadType((prev) => [...prev, typeOption]);
                                          } else {
                                            setLeadType((prev) => prev.filter((t) => t !== typeOption));
                                          }
                                        }}
                                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                      />
                                      <span>{typeOption}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Row 6: Salesperson Ownership */}
                            <div className="md:col-span-2">
                              <label htmlFor="owner" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                                Salesperson Ownership <span className="text-rose-500">*</span>
                              </label>
                              <div className="relative">
                                <select
                                  id="owner"
                                  value={owner}
                                  onChange={(e) => setOwner(e.target.value)}
                                  className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition appearance-none cursor-pointer bg-white dark:bg-slate-900 font-semibold"
                                >
                                  <option value="">UNASSIGNED</option>
                                  {salespeople.map((person) => (
                                    <option key={person.name} value={person.name}>
                                      {person.name} {person.email ? `(${person.email})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <ChevronDown className="w-4 h-4 absolute right-3 top-2.5 text-slate-400 pointer-events-none" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* FOLLOW-UP & RECURRING TASKS CONTAINER CARD */}
                        {(() => {
                          const isFollowUpActive = Boolean(followUpDate) || followUpCompleted;
                          return (
                            <div className={`p-4 rounded-xl border-2 transition-all duration-200 mb-5 shadow-xs ${
                              isFollowUpActive
                                ? 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-400 dark:border-emerald-700/60'
                                : 'bg-sky-50/80 dark:bg-sky-950/30 border-sky-300 dark:border-sky-800/60'
                            }`}>
                              <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-200/80 dark:border-slate-800">
                                <h3 className={`text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 ${
                                  isFollowUpActive ? 'text-emerald-800 dark:text-emerald-300' : 'text-sky-800 dark:text-sky-300'
                                }`}>
                                  <Clock className="w-4 h-4" /> FOLLOW-UP &amp; RECURRING TASKS
                                </h3>
                                <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded ${
                                  isFollowUpActive
                                    ? 'bg-emerald-200/60 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
                                    : 'bg-sky-200/60 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200'
                                }`}>
                                  {isFollowUpActive ? 'Active Follow-Up Scheduled' : 'No Follow-Up Set (Default)'}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="followup_date" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                                    Target Follow-Up Date
                                    <Tooltip text="Date when the next outreach or follow-up task is due." />
                                  </label>
                                  <div className="relative">
                                    <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                                    <input
                                      type="date"
                                      id="followup_date"
                                      value={followUpDate ?? ''}
                                      onChange={(e) => {
                                        setFollowUpDate(e.target.value);
                                        if (e.target.value) {
                                          setFollowUpCompleted(false); // Uncheck Current Follow-up Completed when new date is entered
                                        }
                                      }}
                                      className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pl-9 pr-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition font-semibold cursor-pointer"
                                    />
                                  </div>
                                </div>

                                <div className="flex items-end">
                                  <label htmlFor="followup_completed" className={`flex items-center gap-2.5 p-2 rounded-lg border cursor-pointer transition w-full select-none ${
                                    followUpCompleted
                                      ? 'border-emerald-500 bg-emerald-100/80 dark:bg-emerald-900/60'
                                      : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900/80 hover:bg-slate-50 dark:hover:bg-slate-900'
                                  }`}>
                                    <input
                                      type="checkbox"
                                      id="followup_completed"
                                      checked={followUpCompleted}
                                      onChange={(e) => setFollowUpCompleted(e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                    />
                                    <div>
                                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">
                                        Mark Current Follow-Up Completed
                                      </span>
                                      <span className="text-[10px] text-slate-600 dark:text-slate-400 font-medium">
                                        Logs entry to Activity History &amp; resets for next cycle
                                      </span>
                                    </div>
                                  </label>
                                </div>

                                <div className="md:col-span-2">
                                  <label htmlFor="followup_notes" className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                                    Follow-Up Notes / Next Outreach
                                    <Tooltip text="Notes and immediate next steps for the upcoming outreach touchpoint." />
                                  </label>
                                  <textarea
                                    id="followup_notes"
                                    value={followUpNotes ?? ''}
                                    onChange={(e) => setFollowUpNotes(e.target.value)}
                                    rows={2}
                                    placeholder="Captures immediate next steps or notes for the upcoming outreach..."
                                    className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400 dark:placeholder:text-slate-500"
                                  />
                                </div>

                                <div className="md:col-span-2 mt-1">
                                  <label htmlFor="sync_to_calendar" className="flex items-center gap-2.5 p-2.5 rounded-lg border border-blue-300 dark:border-blue-900/60 bg-white/90 dark:bg-blue-950/30 hover:bg-blue-50/60 dark:hover:bg-blue-900/40 cursor-pointer transition select-none">
                                    <input
                                      type="checkbox"
                                      id="sync_to_calendar"
                                      checked={syncToCalendar}
                                      onChange={(e) => setSyncToCalendar(e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <div className="flex-1">
                                      <span className="text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                        Create Google Calendar Event &amp; Email Invite for Lead Owner
                                      </span>
                                      <span className="text-[10px] text-slate-600 dark:text-slate-400 block mt-0.5">
                                        Automatically creates a Google Calendar event on the follow-up date and sends an email invite to the owner: <strong className="text-slate-800 dark:text-slate-200">{salespeople.find(p => p.name === owner)?.email || (owner.includes('@') ? owner : owner) || 'Lead Owner'}</strong>
                                      </span>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* MULTI-LANE QUOTING & DELIVERY SPECIFICATIONS CONTAINERS (CHANGE 6, 7, 8) */}
                        <div className="space-y-4 mb-5">
                          {lanes.map((lane, laneIdx) => {
                            const isLaneApproved = lane.approvalStatus === 'approved';
                            const isLanePending = (lane.approvalStatus === 'sent' || Boolean(lane.sentToOpsAt)) && !isLaneApproved;
                            const lanePayTerms = lane.paymentTerms ?? (laneIdx === 0 ? paymentTerms : '');
                            const laneDepAmt = lane.depositAmount ?? (laneIdx === 0 ? depositAmount : null);
                            const laneHasFsc = (lane.fuelSurchargePercent || 0) > 0 && !lane.noFuelSurcharge;
                            const laneDealVal = calculateEstimatedDealValue(
                              lane.dollarAmount || 0,
                              lane.uom || '',
                              lane.handlingFee || 0,
                              Boolean(lane.noHandlingFee),
                              laneHasFsc,
                              lane.serviceFrequency || '',
                              lane.minimumShipmentAmount || 0,
                              lane.forecastVolume || 1,
                              lane.fuelSurchargePercent || 0,
                              Boolean(lane.noFuelSurcharge)
                            );
                            const laneFullDeliveryView = formatFullDeliveryView(lane.street || '', lane.city || '', lane.state || '', lane.zipCode || '');
                            const laneFullQuoteView = formatFullQuoteView(
                              lane.dollarAmount || 0,
                              lane.uom || '',
                              lane.handlingFee || 0,
                              Boolean(lane.noHandlingFee),
                              laneHasFsc,
                              lane.serviceFrequency || '',
                              lane.forecastVolume || 1,
                              lane.fuelSurchargePercent || 0,
                              Boolean(lane.noFuelSurcharge),
                              lanePayTerms,
                              laneDepAmt ?? undefined
                            );

                            return (
                              <div
                                key={lane.id || `lane-${laneIdx}`}
                                className={`p-4 rounded-xl border-2 transition-all duration-200 shadow-xs ${
                                  isLaneApproved
                                    ? 'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-400 dark:border-emerald-700/60'
                                    : 'bg-orange-50/80 dark:bg-amber-950/30 border-orange-300 dark:border-amber-700/60'
                                }`}
                              >
                                {/* Top Status Header Banner */}
                                <div className="flex flex-wrap items-center justify-between pb-3 mb-4 border-b border-slate-200/80 dark:border-slate-800 gap-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-black uppercase tracking-wider">
                                      LANE {laneIdx + 1}
                                    </span>
                                    {isLaneApproved ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200 text-xs font-extrabold uppercase tracking-wide">
                                        <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                        Approved
                                      </span>
                                    ) : isLanePending ? (
                                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-200 text-xs font-extrabold uppercase tracking-wide">
                                        <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                        Pending Ops Review
                                      </span>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-orange-100 dark:bg-amber-900/60 text-orange-800 dark:text-amber-200 text-xs font-extrabold uppercase tracking-wide">
                                          <ShieldAlert className="w-4 h-4 text-orange-600 dark:text-amber-400" />
                                          Pending Operations Approval
                                        </span>
                                      </div>
                                    )}
                                    {isLaneApproved && lane.approvedBy && (
                                      <span className="text-[11px] text-slate-600 dark:text-slate-400 font-medium">
                                        Approved by <strong className="text-slate-800 dark:text-slate-200">{lane.approvedBy}</strong>{' '}
                                        {lane.approvedAt && `on ${new Date(lane.approvedAt).toLocaleDateString()}`}
                                      </span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {isLaneApproved && (
                                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-200/60 dark:bg-emerald-900/40 px-2 py-0.5 rounded">
                                        Edits Logged to Activity
                                      </span>
                                    )}
                                    {lanes.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveLane(laneIdx)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-rose-100 hover:bg-rose-200 dark:bg-rose-950/60 dark:hover:bg-rose-900/80 text-rose-700 dark:text-rose-300 text-xs font-bold transition cursor-pointer"
                                        title={`Remove Lane ${laneIdx + 1}`}
                                      >
                                        <Trash2 className="w-3.5 h-3.5" /> Remove Lane
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* 1. Address & Dynamic Delivery View */}
                                <div className="mb-5 pb-4 border-b border-slate-200/60 dark:border-slate-800/60">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                    <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-widest flex items-center gap-1.5">
                                      <MapPin className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Physical Delivery Address &amp; Auto-View
                                    </h3>
                                    {laneIdx === 0 && (
                                      <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer select-none bg-indigo-50/70 dark:bg-indigo-950/40 px-2.5 py-1 rounded-md border border-indigo-200/80 dark:border-indigo-800/60">
                                        <input
                                          type="checkbox"
                                          checked={sameAsConsigneeAddress}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSameAsConsigneeAddress(checked);
                                            if (checked && location) updateLane(0, { street: location });
                                          }}
                                          className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        />
                                        <span>Same as Consignee Address</span>
                                      </label>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <div className="md:col-span-2">
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                        Delivery Street Address
                                      </label>
                                      <input
                                        type="text"
                                        value={lane.street}
                                        onChange={(e) => {
                                          updateLane(laneIdx, { street: e.target.value });
                                          if (laneIdx === 0 && sameAsConsigneeAddress) setSameAsConsigneeAddress(false);
                                        }}
                                        placeholder="e.g. 123 Main St"
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Delivery City</label>
                                      <input
                                        type="text"
                                        value={lane.city}
                                        onChange={(e) => updateLane(laneIdx, { city: e.target.value })}
                                        placeholder="e.g. Palm City"
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Delivery State</label>
                                        <input
                                          type="text"
                                          value={lane.state}
                                          onChange={(e) => updateLane(laneIdx, { state: e.target.value.toUpperCase() })}
                                          placeholder="FL"
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 uppercase"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Delivery ZIP Code</label>
                                        <input
                                          type="text"
                                          value={lane.zipCode}
                                          onChange={(e) => updateLane(laneIdx, { zipCode: e.target.value })}
                                          placeholder="34990"
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                        />
                                      </div>
                                    </div>

                                    {/* Associated Shipper Name & Associated Consignee Name */}
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                        Associated Shipper Name
                                      </label>
                                      <input
                                        type="text"
                                        value={lane.shipperName}
                                        onChange={(e) => updateLane(laneIdx, { shipperName: e.target.value })}
                                        placeholder="e.g. Miami Logistics Hub"
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      />
                                    </div>

                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                          Associated Consignee Name
                                        </label>
                                        {laneIdx === 0 && (
                                          <label className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                                            <input
                                              type="checkbox"
                                              checked={sameAsCompany}
                                              onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSameAsCompany(checked);
                                                if (checked) updateLane(0, { consigneeName: company || '' });
                                              }}
                                              className="w-3 h-3 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                            />
                                            <span>Same as Company Name</span>
                                          </label>
                                        )}
                                      </div>
                                      <input
                                        type="text"
                                        value={lane.consigneeName}
                                        onChange={(e) => {
                                          updateLane(laneIdx, { consigneeName: e.target.value });
                                          if (laneIdx === 0 && sameAsCompany) setSameAsCompany(false);
                                        }}
                                        placeholder="e.g. Consignee Business Name"
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      />
                                    </div>

                                    {/* Rate Agreement Bill-To Role & Consignee Address (when Bill To/Shipper) */}
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-2 pt-1">
                                      <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                          Rate Agreement Role
                                        </label>
                                        <select
                                          value={lane.rateAgreementRole || 'Bill To/Consignee'}
                                          onChange={(e) => {
                                            const nextRole = e.target.value as 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To';
                                            updateLane(laneIdx, { rateAgreementRole: nextRole });
                                            if (laneIdx === 0) {
                                              setBillToType(nextRole);
                                              setUseConsigneeAsBillTo(nextRole === 'Bill To/Consignee');
                                              setUseShipperAsBillTo(nextRole === 'Bill To/Shipper');
                                            }
                                          }}
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                        >
                                          <option value="Bill To/Consignee">Bill To/Consignee</option>
                                          <option value="Bill To/Shipper">Bill To/Shipper</option>
                                          <option value="Bill To">Bill To</option>
                                        </select>
                                      </div>
                                      {lane.rateAgreementRole === 'Bill To/Shipper' && (
                                        <>
                                          <div>
                                            <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                              Consignee City
                                            </label>
                                            <input
                                              type="text"
                                              value={lane.consigneeCity || ''}
                                              onChange={(e) => updateLane(laneIdx, { consigneeCity: e.target.value })}
                                              placeholder="e.g. Miami"
                                              className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                              Consignee State
                                            </label>
                                            <input
                                              type="text"
                                              value={lane.consigneeState || ''}
                                              onChange={(e) => updateLane(laneIdx, { consigneeState: e.target.value.toUpperCase() })}
                                              placeholder="FL"
                                              className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 uppercase"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                                              Consignee ZIP
                                            </label>
                                            <input
                                              type="text"
                                              value={lane.consigneeZip || ''}
                                              onChange={(e) => updateLane(laneIdx, { consigneeZip: e.target.value })}
                                              placeholder="33166"
                                              className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  <div className="p-3 bg-white/80 dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[10px] font-extrabold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Generated Full Delivery View</div>
                                      <code className="text-xs font-mono font-bold text-blue-700 dark:text-blue-400 block break-all">
                                        {laneFullDeliveryView || 'Street | City | State | Zip Code'}
                                      </code>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const fullAddress = [lane.street, lane.city, lane.state, lane.zipCode].filter(Boolean).join(', ');
                                        const query = encodeURIComponent(fullAddress || 'Armellini Logistics');
                                        window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
                                      }}
                                      className="shrink-0 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold text-xs rounded-lg shadow-xs transition flex items-center gap-1.5 cursor-pointer"
                                      title="Open Google Maps for this physical delivery address"
                                    >
                                      <MapPin className="w-3.5 h-3.5" /> Open Map
                                    </button>
                                  </div>
                                </div>

                                {/* 2. Multi-Select Logistics Metadata */}
                                <div className="mb-5 pb-4 border-b border-slate-200/60 dark:border-slate-800/60">
                                  <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                    <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Multi-Select Operations &amp; Delivery Specs
                                  </h3>

                                  <div className="space-y-3">
                                    {/* Service Types */}
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Service Types</label>
                                      <div className="flex flex-wrap gap-1.5">
                                        {SERVICE_TYPES.map((st) => {
                                          const currentServices = lane.serviceTypes || [];
                                          const active = currentServices.includes(st);
                                          return (
                                            <button
                                              key={st}
                                              type="button"
                                              onClick={() =>
                                                updateLane(laneIdx, {
                                                  serviceTypes: active ? currentServices.filter((s) => s !== st) : [...currentServices, st],
                                                })
                                              }
                                              className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition cursor-pointer ${
                                                active
                                                  ? 'border-blue-600 bg-blue-600 text-white'
                                                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'
                                              }`}
                                            >
                                              {st}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Industries */}
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Industries</label>
                                      <div className="flex flex-wrap gap-1.5">
                                        {INDUSTRIES.map((ind) => {
                                          const currentIndustries = lane.industries || [];
                                          const active = currentIndustries.includes(ind);
                                          return (
                                            <button
                                              key={ind}
                                              type="button"
                                              onClick={() =>
                                                updateLane(laneIdx, {
                                                  industries: active ? currentIndustries.filter((i) => i !== ind) : [...currentIndustries, ind],
                                                })
                                              }
                                              className={`px-2.5 py-1 text-xs font-semibold rounded-md border transition cursor-pointer ${
                                                active
                                                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300'
                                                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400'
                                              }`}
                                            >
                                              {ind}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* Outbound States & Warehouses */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Outbound States</label>
                                        <div className="flex gap-1.5">
                                          {OUTBOUND_STATES.map((st) => {
                                            const currentStates = lane.outboundStates || [];
                                            const active = currentStates.includes(st);
                                            return (
                                              <button
                                                key={st}
                                                type="button"
                                                onClick={() =>
                                                  updateLane(laneIdx, {
                                                    outboundStates: active ? currentStates.filter((s) => s !== st) : [...currentStates, st],
                                                  })
                                                }
                                                className={`px-3 py-1 text-xs font-extrabold rounded-md border transition cursor-pointer ${
                                                  active
                                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300'
                                                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600'
                                                }`}
                                              >
                                                {st}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>

                                      <div>
                                        <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Outbound Warehouses</label>
                                        <div className="flex flex-wrap gap-1.5">
                                          {OUTBOUND_WAREHOUSES.map((wh) => {
                                            const currentWarehouses = lane.outboundWarehouses || [];
                                            const active = currentWarehouses.includes(wh);
                                            return (
                                              <button
                                                key={wh}
                                                type="button"
                                                onClick={() =>
                                                  updateLane(laneIdx, {
                                                    outboundWarehouses: active ? currentWarehouses.filter((w) => w !== wh) : [...currentWarehouses, wh],
                                                  })
                                                }
                                                className={`px-2.5 py-1 text-xs font-bold rounded-md border transition cursor-pointer ${
                                                  active
                                                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300'
                                                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600'
                                                }`}
                                              >
                                                {wh}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Days of Service */}
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Days of Service (DOS)</label>
                                      <div className="flex flex-wrap gap-1.5">
                                        {DAYS_OF_SERVICE.map((day) => {
                                          const currentDays = lane.daysOfService || [];
                                          const active = currentDays.includes(day);
                                          return (
                                            <button
                                              key={day}
                                              type="button"
                                              onClick={() =>
                                                updateLane(laneIdx, {
                                                  daysOfService: active ? currentDays.filter((d) => d !== day) : [...currentDays, day],
                                                })
                                              }
                                              className={`px-2 py-1 text-xs font-semibold rounded-md border transition cursor-pointer ${
                                                active
                                                  ? 'border-blue-600 bg-blue-600 text-white'
                                                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600'
                                              }`}
                                            >
                                              {day.slice(0, 3)}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* 3. Quoting Matrix & Estimated Deal Value */}
                                <div>
                                  <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <Calculator className="w-4 h-4 text-indigo-600 dark:text-indigo-400" /> Quoting Matrix &amp; Estimated Deal Value
                                  </h3>

                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Forecast Volume</label>
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={lane.forecastVolume}
                                        onChange={(e) => updateLane(laneIdx, { forecastVolume: Math.max(1, Math.round(Number(e.target.value))) })}
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-mono font-bold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Dollar Amount ($)</label>
                                      <div className="relative">
                                        <span className="absolute left-2.5 top-1.5 text-slate-400 text-sm font-bold">$</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="any"
                                          value={lane.dollarAmount ?? 0}
                                          onChange={(e) => updateLane(laneIdx, { dollarAmount: Number(e.target.value) })}
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pl-6 pr-2 py-1.5 text-sm font-mono font-bold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                        />
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Unit of Measure (UOM)</label>
                                      <select
                                        value={lane.uom || 'Cube'}
                                        onChange={(e) => updateLane(laneIdx, { uom: e.target.value as UnitOfMeasure })}
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      >
                                        {UNITS_OF_MEASURE.map((u) => (
                                          <option key={u} value={u}>{u}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Service Frequency</label>
                                      <select
                                        value={lane.serviceFrequency || 'Weekly'}
                                        onChange={(e) => updateLane(laneIdx, { serviceFrequency: e.target.value as QuoteFrequency })}
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                      >
                                        {QUOTE_FREQUENCIES.map((fq) => (
                                          <option key={fq} value={fq}>{fq}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Handling Fee ($)</label>
                                      <div className="flex gap-2 items-center">
                                        <input
                                          type="number"
                                          min="0"
                                          step="any"
                                          value={lane.handlingFee ?? 0}
                                          onChange={(e) => updateLane(laneIdx, { handlingFee: Number(e.target.value) })}
                                          disabled={lane.noHandlingFee}
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-mono text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 disabled:opacity-60"
                                        />
                                        <label className="flex items-center gap-1 shrink-0 text-xs font-bold text-slate-600 dark:text-slate-400 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(lane.noHandlingFee)}
                                            onChange={(e) => updateLane(laneIdx, { noHandlingFee: e.target.checked })}
                                          />
                                          <span>No-Fee</span>
                                        </label>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Fuel Surcharge (%)</label>
                                      <div className="flex gap-2 items-center">
                                        <div className="relative w-full">
                                          <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={lane.fuelSurchargePercent ?? 0}
                                            onChange={(e) => updateLane(laneIdx, { fuelSurchargePercent: Number(e.target.value) })}
                                            disabled={lane.noFuelSurcharge}
                                            className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pr-6 pl-3 py-1.5 text-sm font-mono text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 disabled:opacity-60"
                                          />
                                          <span className="absolute right-2.5 top-1.5 text-slate-400 text-sm font-bold">%</span>
                                        </div>
                                        <label className="flex items-center gap-1 shrink-0 text-xs font-bold text-slate-600 dark:text-slate-400 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={Boolean(lane.noFuelSurcharge)}
                                            onChange={(e) => updateLane(laneIdx, { noFuelSurcharge: e.target.checked })}
                                          />
                                          <span>No-FSC</span>
                                        </label>
                                      </div>
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Minimum Shipment Amount ($)</label>
                                      <div className="relative">
                                        <span className="absolute left-2.5 top-1.5 text-slate-400 text-sm font-bold">$</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="any"
                                          value={lane.minimumShipmentAmount || ''}
                                          placeholder="0.00"
                                          onChange={(e) => {
                                            const val = Math.max(0, Number(e.target.value));
                                            updateLane(laneIdx, { minimumShipmentAmount: val });
                                            if (laneIdx === 0) {
                                              setMinimumShipmentAmount(val);
                                              setMinimumMonthlyAmount(val);
                                            }
                                          }}
                                          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 pl-6 pr-2 py-1.5 text-sm font-mono font-bold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                        />
                                      </div>
                                    </div>

                                    {/* Payment Terms Dropdown + Conditional Deposit Amount (Change 7) */}
                                    <div>
                                      <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Payment Terms</label>
                                      <select
                                        value={PAYMENT_TERMS_OPTIONS.includes(lanePayTerms as any) ? lanePayTerms : ''}
                                        onChange={(e) => {
                                          const nextTerms = e.target.value;
                                          updateLane(laneIdx, {
                                            paymentTerms: nextTerms,
                                            depositAmount: nextTerms === 'Net 7 w/ Deposit' ? laneDepAmt : null,
                                          });
                                          if (laneIdx === 0) {
                                            setPaymentTerms(nextTerms);
                                            if (nextTerms !== 'Net 7 w/ Deposit') setDepositAmount(null);
                                          }
                                        }}
                                        className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm font-semibold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 cursor-pointer"
                                      >
                                        <option value="">Select Payment Terms...</option>
                                        {PAYMENT_TERMS_OPTIONS.map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                      {lanePayTerms === 'Net 7 w/ Deposit' && (
                                        <div className="mt-2">
                                          <label className="block text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-1">
                                            Deposit Amount ($) <span className="text-rose-500">*</span>
                                          </label>
                                          <div className="relative">
                                            <span className="absolute left-2.5 top-1.5 text-slate-400 text-sm font-bold">$</span>
                                            <input
                                              type="number"
                                              min="0.01"
                                              step="0.01"
                                              required
                                              value={laneDepAmt !== null && laneDepAmt !== undefined && laneDepAmt > 0 ? laneDepAmt : ''}
                                              placeholder="0.00"
                                              onChange={(e) => {
                                                const val = e.target.value ? Number(e.target.value) : null;
                                                updateLane(laneIdx, { depositAmount: val });
                                                if (laneIdx === 0) setDepositAmount(val);
                                              }}
                                              className="block w-full rounded-md border border-amber-400 dark:border-amber-700 pl-6 pr-2 py-1 text-sm font-mono font-bold text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    <div>
                                      <label className="block text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-1">Estimated Deal Value (Mo)</label>
                                      <div className="p-1.5 border rounded-md font-mono font-extrabold text-sm flex items-center justify-between transition-colors bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300">
                                        <span>${laneDealVal.toLocaleString()} / mo</span>
                                        <span className="text-[10px] uppercase font-sans bg-emerald-200 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-200 px-1.5 py-0.5 rounded">
                                          Auto Calculated
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="p-2.5 bg-white/80 dark:bg-slate-900/80 border border-slate-200/80 dark:border-slate-800 rounded-md mb-4">
                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Generated Full Quote View</div>
                                    <code className="text-xs font-mono font-bold text-indigo-700 dark:text-indigo-400 block break-all">
                                      {laneFullQuoteView || 'Quote Details Formula View'}
                                    </code>
                                  </div>
                                </div>

                                {/* 4. Operations / Routing Gatekeeper Section */}
                                <div className="pt-4 border-t border-slate-200/80 dark:border-slate-800">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/90 dark:bg-slate-900/90 p-3.5 rounded-lg border border-slate-200 dark:border-slate-800 shadow-xs">
                                    <div className="flex-1">
                                      <label className="block text-[11px] font-extrabold text-slate-800 dark:text-slate-200 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                        <ShieldCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                        Operations / Routing — Lane {laneIdx + 1} Gatekeeper
                                      </label>
                                      <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition select-none ${
                                        isLaneApproved
                                          ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40'
                                          : 'border-amber-300 bg-amber-50 dark:bg-amber-950/40'
                                      }`}>
                                        <input
                                          type="checkbox"
                                          checked={isLaneApproved}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            const currentUser = auth.currentUser?.displayName || auth.currentUser?.email || 'Operations User';
                                            const now = new Date().toISOString();
                                            updateLane(laneIdx, {
                                              approvalStatus: checked ? 'approved' : (lane.sentToOpsAt ? 'sent' : 'pending'),
                                              approvedBy: checked ? currentUser : undefined,
                                              approvedAt: checked ? now : undefined,
                                            });
                                            if (laneIdx === 0) {
                                              setRoutingOpsApproved(checked);
                                              setRoutingApprovedBy(checked ? currentUser : '');
                                              setRoutingApprovedAt(checked ? now : '');
                                            }
                                          }}
                                          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                        />
                                        <div className="flex-1">
                                          <span className={`text-xs font-bold flex items-center gap-1 ${
                                            isLaneApproved ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-900 dark:text-amber-300'
                                          }`}>
                                            {isLaneApproved ? <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> : <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
                                            Routing Dept: Operations Approved (Lane {laneIdx + 1})
                                          </span>
                                          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 space-y-0.5">
                                            {isLaneApproved ? (
                                              <>
                                                <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                                                  Approved by {lane.approvedBy || 'Operations'}{' '}
                                                  {lane.approvedAt && `on ${new Date(lane.approvedAt).toLocaleString()}`}
                                                </p>
                                                {lane.sentToOpsBy && (
                                                  <p>
                                                    Requested by {lane.sentToOpsBy}{' '}
                                                    {lane.sentToOpsAt && `on ${new Date(lane.sentToOpsAt).toLocaleString()}`}
                                                  </p>
                                                )}
                                              </>
                                            ) : (
                                              <p>Quoting Restricted until approved by Operations</p>
                                            )}
                                          </div>
                                        </div>
                                      </label>
                                    </div>

                                    {/* Button to Send Email Request to Operations */}
                                    <div className="shrink-0 flex flex-col items-end justify-center">
                                      <button
                                        type="button"
                                        disabled={sendingOpsEmail}
                                        onClick={() => handleSendOpsEmail(laneIdx)}
                                        className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold text-xs rounded-lg shadow-sm transition flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                                      >
                                        {sendingOpsEmail ? (
                                          <>
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending to Operations...
                                          </>
                                        ) : (
                                          <>
                                            <Send className="w-3.5 h-3.5" /> Send to Operations
                                          </>
                                        )}
                                      </button>
                                      {lane.sentToOpsBy && (
                                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 text-right font-medium">
                                          Requested by {lane.sentToOpsBy}{' '}
                                          {lane.sentToOpsAt && `(${new Date(lane.sentToOpsAt).toLocaleDateString()})`}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* + Add A Lane Button */}
                          <div className="flex items-center justify-between pt-1">
                            <button
                              type="button"
                              onClick={handleAddLane}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-indigo-400 dark:border-indigo-700 bg-indigo-50/60 hover:bg-indigo-100/80 dark:bg-indigo-950/30 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs font-extrabold uppercase tracking-wider transition cursor-pointer shadow-2xs"
                            >
                              + Add A Lane
                            </button>
                            {lanes.length > 1 && (
                              <span className="text-xs font-mono font-extrabold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800">
                                Total Estimated Deal Value ({lanes.length} Lanes): $
                                {lanes
                                  .reduce(
                                    (sum, l) =>
                                      sum +
                                      calculateEstimatedDealValue(
                                        l.dollarAmount || 0,
                                        l.uom || '',
                                        l.handlingFee || 0,
                                        Boolean(l.noHandlingFee),
                                        (l.fuelSurchargePercent || 0) > 0 && !l.noFuelSurcharge,
                                        l.serviceFrequency || '',
                                        l.minimumShipmentAmount || 0,
                                        l.forecastVolume || 1,
                                        l.fuelSurchargePercent || 0,
                                        Boolean(l.noFuelSurcharge)
                                      ),
                                    0
                                  )
                                  .toLocaleString()}{' '}
                                / mo
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Phase 2: Core Pipeline Milestone Dates */}
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                          <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" /> Core Pipeline Milestone Dates (Forward Momentum)
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Inquiry Date (&rarr; LEAD)</label>
                              <input
                                type="date"
                                value={inquiryDate}
                                onChange={(e) => setInquiryDate(e.target.value)}
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Proposal Date (&rarr; QUOTED)</label>
                              <input
                                type="date"
                                value={proposalDate}
                                onChange={(e) => setProposalDate(e.target.value)}
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">Accepted Date (&rarr; APPROVED)</label>
                              <input
                                type="date"
                                value={acceptedDate}
                                onChange={(e) => setAcceptedDate(e.target.value)}
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider mb-1">First Shipment Date (&rarr; FINALIZING &amp; 30-Day Follow-Up Timer)</label>
                              <input
                                type="date"
                                value={firstShipmentDate}
                                onChange={(e) => setFirstShipmentDate(e.target.value)}
                                className="block w-full rounded-md border border-emerald-300 dark:border-emerald-800 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-emerald-50/50 dark:bg-emerald-950/30 font-bold cursor-pointer"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                Final Follow-Up Complete (&rarr; COMPLETE)
                                <Tooltip text="Mark the 1-month final follow-up as complete. Hides the dashboard follow-up window countdown and triggers 1st-month revenue calculation from A4." />
                              </label>
                              <input
                                type="date"
                                value={finalFollowUpDate}
                                onChange={(e) => setFinalFollowUpDate(e.target.value)}
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-900 font-semibold cursor-pointer"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1 flex items-center justify-between">
                                <span>Monthly Revenue</span>
                                <span className="text-[9px] font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950 px-1.5 py-0.5 rounded border border-blue-200 dark:border-blue-800">
                                  A4 Auto-Calculated
                                </span>
                              </label>
                              <div className="relative">
                                <DollarSign className="w-4 h-4 absolute left-3 top-2 text-slate-400" />
                                <input
                                  type="text"
                                  readOnly
                                  disabled
                                  value={monthlyRevenue ? monthlyRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                  className="block w-full rounded-md border border-slate-200 dark:border-slate-800 pl-8 pr-3 py-1.5 text-sm font-mono font-bold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-900/60 cursor-not-allowed select-none"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Date-Stamped Onboarding Checkboxes */}
                          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                            <span className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2.5">
                              Onboarding Checklist (Informational &mdash; Does Not Change Status or Category)
                            </span>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                              {([
                                {
                                  label: 'Credit App Sent',
                                  dateVal: creditAppSentAt,
                                  setDateVal: setCreditAppSentAt,
                                  byVal: creditAppSentBy,
                                  setByVal: setCreditAppSentBy,
                                },
                                {
                                  label: 'Rate Agreement Sent',
                                  dateVal: rateAgreementSentAt,
                                  setDateVal: setRateAgreementSentAt,
                                  byVal: rateAgreementSentBy,
                                  setByVal: setRateAgreementSentBy,
                                },
                                {
                                  label: 'Internal Emails Sent',
                                  dateVal: internalEmailsSentAt,
                                  setDateVal: setInternalEmailsSentAt,
                                  byVal: internalEmailsSentBy,
                                  setByVal: setInternalEmailsSentBy,
                                },
                                {
                                  label: 'First Delivery Follow Up',
                                  dateVal: firstDeliveryFollowUpAt,
                                  setDateVal: setFirstDeliveryFollowUpAt,
                                  byVal: firstDeliveryFollowUpBy,
                                  setByVal: setFirstDeliveryFollowUpBy,
                                },
                              ] as const).map((chk) => {
                                const isChecked = Boolean(chk.dateVal && chk.dateVal.trim());
                                return (
                                  <div
                                    key={chk.label}
                                    className={`p-2.5 rounded-lg border flex flex-col gap-1.5 transition ${
                                      isChecked
                                        ? 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800'
                                        : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              const today = new Date().toISOString().slice(0, 10);
                                              chk.setDateVal(today);
                                              chk.setByVal(auth.currentUser?.displayName || auth.currentUser?.email || 'CRM User');
                                            } else {
                                              chk.setDateVal('');
                                              chk.setByVal('');
                                            }
                                          }}
                                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                        />
                                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                          {chk.label}
                                        </span>
                                      </label>
                                      {isChecked && (
                                        <input
                                          type="date"
                                          value={chk.dateVal}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            chk.setDateVal(v);
                                            if (!v) chk.setByVal('');
                                          }}
                                          className="rounded border border-emerald-300 dark:border-emerald-700 px-2 py-0.5 text-xs font-mono font-bold text-emerald-900 dark:text-emerald-200 bg-white dark:bg-slate-900 cursor-pointer"
                                        />
                                      )}
                                    </div>
                                    {isChecked && chk.byVal && (
                                      <span className="text-[10px] font-mono text-emerald-700 dark:text-emerald-400 pl-6">
                                        Checked on {chk.dateVal} by {chk.byVal}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Pipeline Disqualification Off-Ramps */}
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                          <h3 className="text-xs font-bold text-rose-600 dark:text-rose-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                            <AlertTriangle className="w-4 h-4" /> Pipeline Removal Off-Ramps (Disqualifiers)
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                            <label className={`p-2.5 rounded-lg border cursor-pointer flex items-center gap-2 select-none transition ${
                              isTransferred ? 'bg-amber-50 dark:bg-amber-950/50 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }`}>
                              <input
                                type="checkbox"
                                checked={isTransferred}
                                onChange={(e) => setIsTransferred(e.target.checked)}
                                className="rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                              />
                              <span className="text-xs font-bold">Transfer (Unqualified)</span>
                            </label>

                            <label className={`p-2.5 rounded-lg border cursor-pointer flex items-center gap-2 select-none transition ${
                              isDeclined ? 'bg-amber-50 dark:bg-amber-950/50 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }`}>
                              <input
                                type="checkbox"
                                checked={isDeclined}
                                onChange={(e) => setIsDeclined(e.target.checked)}
                                className="rounded border-amber-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                              />
                              <span className="text-xs font-bold">Declined (Unqualified)</span>
                            </label>

                            <label className={`p-2.5 rounded-lg border cursor-pointer flex items-center gap-2 select-none transition ${
                              isNotAwarded ? 'bg-rose-50 dark:bg-rose-950/50 border-rose-300 dark:border-rose-700 text-rose-900 dark:text-rose-200' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }`}>
                              <input
                                type="checkbox"
                                checked={isNotAwarded}
                                onChange={(e) => setIsNotAwarded(e.target.checked)}
                                className="rounded border-rose-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                              />
                              <span className="text-xs font-bold">Not Awarded (Lose)</span>
                            </label>

                            <label className={`p-2.5 rounded-lg border cursor-pointer flex items-center gap-2 select-none transition ${
                              is30DayUnresponsive ? 'bg-purple-50 dark:bg-purple-950/50 border-purple-300 dark:border-purple-700 text-purple-900 dark:text-purple-200' : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300'
                            }`}>
                              <input
                                type="checkbox"
                                checked={is30DayUnresponsive}
                                onChange={(e) => {
                                  const val = e.target.checked;
                                  setIs30DayUnresponsive(val);
                                  if (val) {
                                    setIsArchived(true);
                                  }
                                }}
                                className="rounded border-purple-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                              />
                              <span className="text-xs font-bold">30-Day Unresponsive (Archive)</span>
                            </label>
                          </div>

                          {/* Dynamic Conditional Reason Dropdowns */}
                          {(isTransferred || isDeclined || isNotAwarded) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                              {isTransferred && (
                                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg">
                                  <label className="block text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-1">Transfer Reason</label>
                                  <select
                                    value={transferredReason}
                                    onChange={(e) => setTransferredReason(e.target.value)}
                                    className="block w-full rounded-md border border-amber-300 dark:border-amber-700 px-3 py-1.5 text-xs font-bold text-amber-950 dark:text-amber-100 bg-white dark:bg-slate-900 cursor-pointer"
                                  >
                                    <option value="Armellini Air Express">Armellini Air Express</option>
                                    <option value="JA Flowers">JA Flowers</option>
                                    <option value="Armellini Freight Brokerage">Armellini Freight Brokerage</option>
                                    <option value="C/O AEL Account">C/O AEL Account</option>
                                  </select>
                                </div>
                              )}

                              {isDeclined && (
                                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg">
                                  <label className="block text-[11px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-1">Declined Reason</label>
                                  <select
                                    value={declinedReason}
                                    onChange={(e) => setDeclinedReason(e.target.value)}
                                    className="block w-full rounded-md border border-amber-300 dark:border-amber-700 px-3 py-1.5 text-xs font-bold text-amber-950 dark:text-amber-100 bg-white dark:bg-slate-900 cursor-pointer"
                                  >
                                    <option value="Delivery Environment">Delivery Environment</option>
                                    <option value="Routing">Routing</option>
                                    <option value="Temperature">Temperature</option>
                                    <option value="Commodity">Commodity</option>
                                    <option value="Credit">Credit</option>
                                  </select>
                                </div>
                              )}

                              {isNotAwarded && (
                                <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-lg">
                                  <label className="block text-[11px] font-bold text-rose-800 dark:text-rose-300 uppercase tracking-wider mb-1">Not Awarded Reason</label>
                                  <select
                                    value={notAwardedReason}
                                    onChange={(e) => setNotAwardedReason(e.target.value)}
                                    className="block w-full rounded-md border border-rose-300 dark:border-rose-700 px-3 py-1.5 text-xs font-bold text-rose-950 dark:text-rose-100 bg-white dark:bg-slate-900 cursor-pointer"
                                  >
                                    <option value="Rate">Rate issue</option>
                                    <option value="DOS">Days of Service (DOS) conflict</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Lead Notes & Quick Notes */}
                        <div>
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label htmlFor="leadNotes" className="block text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                <FileText className="w-3.5 h-3.5" /> Lead Notes
                                <Tooltip text="Add or update editable notes for this lead." />
                              </label>
                              <textarea
                                id="leadNotes"
                                value={leadNotes}
                                onChange={(e) => {
                                  setLeadNotes(e.target.value);
                                  e.target.style.height = 'auto';
                                  e.target.style.height = `${Math.max(80, e.target.scrollHeight)}px`;
                                }}
                                rows={Math.max(3, (leadNotes || '').split('\n').length)}
                                placeholder="Add or update notes for this lead"
                                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition placeholder:text-slate-400"
                              />
                            </div>

                            <div className="pt-3 border-t border-slate-100 dark:border-slate-900/60 mt-3">
                              <label htmlFor="quickNotes" className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                                <FileText className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> Quick Notes (Meeting & Call Logs)
                                <Tooltip text="Internal chronological notes detailing all call interactions and progress logs." />
                              </label>
                              {quickNotes.trim() && (
                                <div className="mb-2 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50/70 dark:bg-slate-900/40 overflow-hidden">
                                  <div className="px-3 py-1.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Note History (locked)</span>
                                    <span className="text-[9px] font-mono text-slate-400">previous notes are preserved automatically</span>
                                  </div>
                                  <div className="p-3 max-h-44 overflow-y-auto text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">
                                    {quickNotes}
                                  </div>
                                </div>
                              )}
                              <div className="border border-slate-300 dark:border-slate-800 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 transition">
                                {/* Format toolbar */}
                                <div className="flex flex-wrap gap-1 items-center bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800/80 p-1.5 select-none">
                                  <button
                                    type="button"
                                    onClick={() => handleFormatText('bold')}
                                    title="Bold Selected Text"
                                    className="p-1 px-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 rounded cursor-pointer transition"
                                  >
                                    B
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleFormatText('italic')}
                                    title="Italicize Selected Text"
                                    className="p-1 px-2 text-xs italic text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 rounded cursor-pointer transition"
                                  >
                                    I
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleFormatText('bullet')}
                                    title="Bullet List"
                                    className="p-1 px-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 rounded cursor-pointer transition flex items-center gap-1 font-semibold"
                                  >
                                    • List
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleFormatText('highlight')}
                                    title="Highlight text"
                                    className="p-1 px-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-800 rounded cursor-pointer transition flex items-center gap-1 font-semibold"
                                  >
                                    ✨ Highlight
                                  </button>
                                  <div className="w-px h-3.5 bg-slate-200 dark:bg-slate-800 mx-1"></div>
                                  <button
                                    type="button"
                                    onClick={() => handleFormatText('time')}
                                    title="Insert Timestamp"
                                    className="p-1 px-2 text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-200 dark:hover:bg-slate-800 rounded cursor-pointer transition flex items-center gap-1"
                                  >
                                    📅 Timestamp
                                  </button>
                                </div>
                                {/* Editor body */}
                                <textarea
                                  id="quickNotes"
                                  value={newNote}
                                  onChange={(e) => setNewNote(e.target.value)}
                                  rows={4}
                                  placeholder="Add a new note — it will be saved with your name, date, and time. Previous notes are never overwritten."
                                  className="block w-full border-0 p-3 text-xs text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-950 focus:ring-0 focus:outline-none placeholder:text-slate-400 dark:placeholder:text-slate-600 font-sans leading-relaxed"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TAB 2: File Attachments & CRM Documents */}
                    {activeTab === 'files' && (
                      <div className="flex flex-col gap-5">
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center justify-between">
                          <div>
                            <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                              <Paperclip className="w-3.5 h-3.5" /> Associated Documents &amp; Signed Forms
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Upload pricing sheets, certificates, or view automatically cross-listed signed agreements and W-9s.
                            </p>
                          </div>
                          {loadingDocuments && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                        </div>

                        {uploadingDocument && (
                          <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                            <span>Uploading document to secure CRM storage...</span>
                          </div>
                        )}

                        {/* File Upload Dropzone */}
                        <div
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          className={`border-2 border-dashed rounded-lg p-6 text-center transition flex flex-col items-center justify-center cursor-pointer ${
                            isDragging
                              ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
                              : 'border-slate-300 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 bg-slate-50/50 dark:bg-slate-900/30 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                          }`}
                          onClick={() => document.getElementById('slideover-file-input')?.click()}
                        >
                          <input
                            type="file"
                            id="slideover-file-input"
                            multiple
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <UploadCloud className={`w-8 h-8 mb-2.5 transition ${isDragging ? 'text-blue-500 scale-110' : 'text-slate-400'}`} />
                          <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            Drag &amp; drop files here, or <span className="text-blue-600 dark:text-blue-400 hover:underline">browse files</span>
                          </p>
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                            Supports PDF, Word, Excel, and images up to 25MB (securely stored in CRM)
                          </p>
                        </div>

                        {/* File & Documents List */}
                        <div className="space-y-2 mt-2">
                          <h4 className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                            CRM Documents ({leadDocuments.length + attachedFiles.filter(f => !leadDocuments.some(d => d.fileName === f)).length})
                          </h4>
                          {leadDocuments.length === 0 && attachedFiles.length === 0 ? (
                            <div className="border border-slate-100 dark:border-slate-800 rounded-lg p-8 text-center bg-slate-50/30 dark:bg-slate-900/20">
                              <FileText className="w-6 h-6 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                              <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No documents attached to this lead yet.</p>
                            </div>
                          ) : (
                            <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1">
                              {/* 1. Database-backed lead documents with badges and authenticated download */}
                              {leadDocuments.map((doc) => {
                                const isPdf = doc.fileName.toLowerCase().endsWith('.pdf');
                                const isImg = doc.mimeType?.startsWith('image/');
                                
                                const getBadge = () => {
                                  switch (doc.documentType) {
                                    case 'signed_agreement':
                                    case 'Signed Agreement':
                                      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">[Signed Agreement]</span>;
                                    case 'w9_form':
                                    case 'W9 Form':
                                      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">[W-9 Form]</span>;
                                    case 'ein_resale_cert':
                                    case 'EIN Resale Cert':
                                      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300">[EIN Resale Cert]</span>;
                                    case 'credit_card_auth':
                                    case 'Credit Card Auth':
                                      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300">[Credit Card Auth]</span>;
                                    default:
                                      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300">[Attachment]</span>;
                                  }
                                };

                                return (
                                  <div
                                    key={doc.id}
                                    className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-900/60 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 transition group"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <div className={`p-1.5 rounded shrink-0 ${
                                        isPdf ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400' :
                                        isImg ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                                      }`}>
                                        <File className="w-4 h-4" />
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          {getBadge()}
                                          <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                                            {doc.fileName}
                                          </p>
                                        </div>
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono mt-0.5">
                                          {doc.fileSize ? `${Math.round(doc.fileSize / 1024)} KB` : 'Document'} &bull; {new Date(doc.createdAt).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!editingLead?.id) return;
                                          downloadLeadDocument(editingLead.id, doc.id, doc.fileName).catch((err) => {
                                            if (showToast) showToast(`Download failed: ${err.message}`, editingLead.id);
                                          });
                                        }}
                                        className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 hover:text-blue-600 rounded transition cursor-pointer"
                                        title="Download Document"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </button>
                                      {(userRole === 'admin' || userRole === 'editor' || userRole === 'sales') && (
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteLeadDocument(doc.id, doc.fileName)}
                                          className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 hover:text-red-500 rounded transition cursor-pointer"
                                          title="Delete document"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}

                              {/* 2. Legacy attached files not yet stored as LeadDocument */}
                              {attachedFiles
                                .filter((fileName) => !leadDocuments.some((d) => d.fileName === fileName))
                                .map((fileName, idx) => (
                                  <div
                                    key={`legacy-${idx}`}
                                    className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-900/60 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 transition group"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <div className="p-1.5 rounded shrink-0 bg-slate-100 dark:bg-slate-800 text-slate-500">
                                        <File className="w-4 h-4" />
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">[Attachment]</span>
                                          <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                                            {fileName}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(idx)}
                                      className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-400 hover:text-red-500 rounded transition shrink-0 cursor-pointer"
                                      title="Remove attachment"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* TAB 3: Online Forms */}
                    {activeTab === 'forms' && (
                      editingLead ? (
                        <LeadFormsTab
                          lead={editingLead}
                          userRole={userRole}
                          currentUserEmail={auth.currentUser?.email || undefined}
                          showToast={(msg, lid) => showToast && showToast(msg, lid || editingLead?.id)}
                          onActivityLogUpdated={(logs) => setLeadActivityLog(logs)}
                          onDocumentsUpdated={(docs) => setLeadDocuments(docs)}
                        />
                      ) : (
                        <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center bg-slate-50/40 dark:bg-slate-900/20">
                          <FileSignature className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                          <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">Save Lead First</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto mt-1">
                            Please save this lead first before generating and dispatching online customer forms.
                          </p>
                        </div>
                      )
                    )}

                    {/* TAB 3: Activity History Timeline */}
                    {activeTab === 'activity' && (
                      <div className="flex flex-col gap-5">
                        <div className="border-b border-slate-100 dark:border-slate-800 pb-3">
                          <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                            <Activity className="w-3.5 h-3.5" /> Historical Activity Log
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Chronological history of recent pipeline movements, status updates, and form edits.
                          </p>
                        </div>

                        {!editingLead ? (
                          <div className="border border-slate-100 dark:border-slate-800 rounded-lg p-8 text-center bg-slate-50/30 dark:bg-slate-900/30">
                            <Clock className="w-6 h-6 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">New Unsaved Lead</p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 max-w-xs mx-auto">
                              No history is available for new unsaved leads. Save this lead first to start recording history logs.
                            </p>
                          </div>
                        ) : !leadActivityLog || leadActivityLog.length === 0 ? (
                          <div className="border border-slate-100 dark:border-slate-800 rounded-lg p-8 text-center bg-slate-50/30 dark:bg-slate-900/30">
                            <Activity className="w-6 h-6 text-slate-300 dark:text-slate-700 mx-auto mb-2" />
                            <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No edits or activity recorded for this lead yet.</p>
                          </div>
                        ) : (
                          <div className="relative border-l border-slate-200 dark:border-slate-800 pl-4 ml-4.5 space-y-6 max-h-[450px] overflow-y-auto pr-1">
                            {leadActivityLog.map((log) => {
                              const isCreated = log.action === 'Lead Created';
                              const isDocReceived =
                                log.action.includes('Document Received') ||
                                log.action.includes('Submitted') ||
                                log.action.includes('Signed');
                              const isDocSent =
                                log.action.includes('Document Sent') ||
                                log.action.includes('Invite Sent') ||
                                log.action.includes('Sent (DocuSeal)') ||
                                log.action.includes('Resent');
                              const isDocOpened = log.action.includes('Opened');
                              const isStatus = log.action.includes('Status') || log.action.includes('Pipeline') || log.action.includes('Bulk');
                              const isQuote = log.action.includes('Quote') || log.action.includes('Attachment') || log.action.includes('File') || log.action.includes('Document Uploaded');

                              // Determine Initials for Avatar
                              let initials = 'SYS';
                              let avatarBg = 'bg-slate-500 text-white';
                              if (log.user && log.user !== 'system') {
                                const userPart = log.user.split('@')[0];
                                const parts = userPart.split('.');
                                if (parts.length >= 2) {
                                  initials = (parts[0][0] + parts[1][0]).toUpperCase();
                                } else {
                                  initials = userPart.slice(0, 2).toUpperCase();
                                }
                                // Pick a deterministic color class
                                const hash = log.user.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                                const colors = [
                                  'bg-blue-600 text-white',
                                  'bg-indigo-600 text-white',
                                  'bg-purple-600 text-white',
                                  'bg-pink-600 text-white',
                                  'bg-emerald-600 text-white',
                                ];
                                avatarBg = colors[hash % colors.length];
                              } else {
                                avatarBg = 'bg-slate-400 dark:bg-slate-700 text-white';
                              }

                              return (
                                <div key={log.id} className="relative flex gap-3.5 group">
                                  {/* Left absolute indicator with custom Avatar */}
                                  <div className="shrink-0 relative z-10 -ml-[28px]">
                                    <div className={`w-6 h-6 rounded-full ${avatarBg} flex items-center justify-center font-mono text-[9px] font-bold shadow-sm ring-4 ring-white dark:ring-slate-950`}>
                                      {initials}
                                    </div>
                                  </div>

                                  {/* Visual Log Node Card */}
                                  <div className="flex-1 bg-slate-50/50 dark:bg-slate-900/30 hover:bg-slate-50 dark:hover:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl p-3.5 transition">
                                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                                      <span className="text-xs font-bold text-slate-800 dark:text-slate-100 flex items-center gap-1.5 flex-wrap">
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          isCreated || isDocReceived ? 'bg-emerald-500' :
                                          isDocSent ? 'bg-indigo-500' :
                                          isDocOpened ? 'bg-sky-500' :
                                          isStatus ? 'bg-amber-500' :
                                          isQuote ? 'bg-blue-500' : 'bg-slate-400'
                                        }`} />
                                        {log.action}
                                        {isDocReceived && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                                            Received
                                          </span>
                                        )}
                                        {isDocSent && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
                                            Sent
                                          </span>
                                        )}
                                        {isDocOpened && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300">
                                            Opened
                                          </span>
                                        )}
                                      </span>
                                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-mono inline-flex items-center gap-1">
                                        <Clock className="w-2.5 h-2.5" />
                                        {new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </div>
                                    <p className="text-[11px] text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
                                      {log.details}
                                    </p>
                                    <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-slate-100/50 dark:border-slate-800/30 text-[9px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider">
                                      <User className="w-2.5 h-2.5" />
                                      <span>Operator: {log.user === 'system' ? 'System Sync' : log.user}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                  </form>
                </div>

                {/* Footer Action Buttons */}
                <div className="flex flex-shrink-0 justify-between items-center px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                  <div>
                    {editingLead && (
                      <button
                        type="button"
                        onClick={handleDuplicateLead}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-xs font-bold text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-100 dark:hover:bg-slate-900 transition cursor-pointer"
                        title="Duplicate lead with contact details only (no rate info)"
                      >
                        <Copy className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Duplicate Lead
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 transition cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      form="lead-form"
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition cursor-pointer"
                    >
                      {editingLead ? 'Save Changes' : 'Create Lead'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
